import type {
    SearchQuery, HolidayPackage, Hotel, Tour, ApiSettings, ApiLogEntry,
    LocationOption, DateOption, NightOption, ProductInfo, Offer,
    BookingRequest, ReservationResult, BookingRecord, ImageProcessingOptions,
    Traveller, SetReservationInfoRequest, BeginTransactionResponse,
    PaymentGroup, BeginPaymentTransactionRequest, PaymentTransactionResponse,
    FlightSearchQuery, FlightOffer, GetCancellationPenaltyRequest, GetCancellationPenaltyResponse,
    CancelReservationRequest, CancelReservationResponse,
    GetPaymentTypesRequest, GetPaymentTypesResponse,
    GetTransportationsRequest, GetTransportationsResponse,
    GetExchangeRatesRequest, GetExchangeRatesResponse,
    CheckPaymentResponse, GetTransactionStatusResponse,
    Language,
    FlightSegment,
    FlightAlternative,
    GetPagingDataRequest,
    PagingOption,

} from '../types';
import { SearchType } from '../types';
import { saveBooking, getWidgetSettings, getWhitelist } from './dataService';
import { resolveAirlineName, getAirlineNameByCode } from '../utils/flightUtils';

// Export helper to map real flight option to flight segment
export const mapFlightSegment = (realFlight: any, serviceAddress: string): FlightSegment => {
    // TourVisio sends airport data in various shapes — handle all
    const getAirportDisplay = (data: any): string => {
        if (!data) return '';
        if (typeof data === 'string') return data;
        // Nested: {airport: {name, code}, city: {name}}
        return data.airport?.name || data.airport?.code || data.name || data.code || data.city?.name || '';
    };

    // TourVisio often puts real times inside segments[0].departure.date / arrival.date
    const firstSeg = realFlight.segments?.[0];
    const depTime = realFlight.departureTime
        || realFlight.departure?.date
        || firstSeg?.departure?.date
        || firstSeg?.departureTime
        || '';
    const arrTime = realFlight.arrivalTime
        || realFlight.arrival?.date
        || firstSeg?.arrival?.date
        || firstSeg?.arrivalTime
        || '';

    // Baggage: check item-level, then segment-level baggageInformations
    const itemBaggage = realFlight.baggageInformations?.[0];
    const segBaggage = firstSeg?.baggageInformations?.[0];
    const baggageSource = itemBaggage || segBaggage;
    let baggage = realFlight.baggage || '';
    if (!baggage && baggageSource) {
        if (baggageSource.weight && (baggageSource.unit || baggageSource.unitType !== undefined)) {
            const unitStr = baggageSource.unit || (baggageSource.unitType === 0 || baggageSource.unitType === '0' ? 'kg' : baggageSource.unitType === 2 || baggageSource.unitType === '2' ? 'Piece' : 'kg');
            baggage = `${baggageSource.weight} ${unitStr}`;
        } else {
            baggage = baggageSource.description || (baggageSource.weight ? `${baggageSource.weight} kg` : '');
        }
    }
    // Final fallback: flightBrandInfo.name or standard 20 kg package charter baggage
    if (!baggage) {
        const brandInfo = realFlight.flightBrandInfo || firstSeg?.flightBrandInfo;
        if (brandInfo?.name) baggage = brandInfo.name;
        else baggage = '20 kg';
    }

    // Airline logo: prefer logoFull over logo
    const airlineLogo = (typeof realFlight.airline === 'object'
        ? (realFlight.airline?.logoFull || realFlight.airline?.logo || '')
        : '') || realFlight.logoUrl || '';

    const formatTime = (isoStr: string | undefined) => {
        if (!isoStr) return '';
        try {
            const d = new Date(isoStr);
            if (isNaN(d.getTime())) {
                const match = isoStr.match(/(\d{2}:\d{2})/);
                return match ? match[1] : isoStr;
            }
            return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
        } catch { return isoStr; }
    };

    const formattedDepTime = formatTime(depTime);
    const formattedArrTime = formatTime(arrTime);

    // Auto-calculate duration if missing
    let finalDuration = realFlight.duration || '';
    if (!finalDuration && formattedDepTime && formattedArrTime) {
        try {
            const [dh, dm] = formattedDepTime.split(':').map(Number);
            const [ah, am] = formattedArrTime.split(':').map(Number);
            if (!isNaN(dh) && !isNaN(dm) && !isNaN(ah) && !isNaN(am)) {
                let depM = dh * 60 + dm;
                let arrM = ah * 60 + am;
                if (arrM < depM) arrM += 24 * 60;
                const diff = arrM - depM;
                if (diff >= 60 && diff <= 900) {
                    const h = Math.floor(diff / 60);
                    const m = diff % 60;
                    finalDuration = m > 0 ? `${h}h ${m}m` : `${h}h`;
                }
            }
        } catch { }
    }

    // Extract airport codes and names separately
    const getCode = (data: any): string => {
        if (!data) return '';
        if (typeof data === 'string') return data;
        return data.airport?.code || data.airport?.id || data.code || '';
    };
    const getName = (data: any): string => {
        if (!data) return '';
        if (typeof data === 'string') return '';
        return data.airport?.name || data.name || data.city?.name || '';
    };

    const depCode = getCode(realFlight.departureAirport) || getCode(realFlight.departure) || (firstSeg ? getCode(firstSeg.departure) : '');
    const depName = getName(realFlight.departure) || (firstSeg ? getName(firstSeg.departure) : '') || realFlight.departureAirportName || '';
    const arrCode = getCode(realFlight.arrivalAirport) || getCode(realFlight.arrival) || (firstSeg ? getCode(firstSeg.arrival) : '');
    const arrName = getName(realFlight.arrival) || (firstSeg ? getName(firstSeg.arrival) : '') || realFlight.arrivalAirportName || '';

    const rawFlightNo = String(realFlight.flightNumber || realFlight.flightNo || firstSeg?.flightNo || firstSeg?.flightNumber || '');
    const isFakeOrGenericNo = !rawFlightNo || /^fake/i.test(rawFlightNo) || /charter/i.test(rawFlightNo);
    const airlineCode = realFlight.airlineCode || realFlight.airline?.code || firstSeg?.airline?.code || (rawFlightNo ? rawFlightNo.replace(/[^A-Za-z]/g, '').substring(0, 2) : '');
    const rawAirlineName = typeof realFlight.airline === 'object' ? (realFlight.airline?.name || '') : (realFlight.airline || '');
    const finalAirlineName = resolveAirlineName(rawAirlineName, airlineCode, rawFlightNo);

    return {
        departureTime: formattedDepTime,
        arrivalTime: formattedArrTime,
        departureAirport: depName || depCode || getAirportDisplay(realFlight.departureAirport) || getAirportDisplay(realFlight.departure) || (firstSeg ? getAirportDisplay(firstSeg.departure) : ''),
        departureAirportCode: depCode,
        departureAirportName: depName,
        arrivalAirport: arrName || arrCode || getAirportDisplay(realFlight.arrivalAirport) || getAirportDisplay(realFlight.arrival) || (firstSeg ? getAirportDisplay(firstSeg.arrival) : ''),
        arrivalAirportCode: arrCode,
        arrivalAirportName: arrName,
        airline: finalAirlineName,
        flightDate: realFlight.departureDate || realFlight.departure?.date || firstSeg?.departure?.date || '',
        logoUrl: airlineLogo,
        duration: finalDuration || '4h 00m',
        flightNumber: isFakeOrGenericNo ? '' : rawFlightNo,
        stops: realFlight.stops || (realFlight.segments ? Math.max(0, realFlight.segments.length - 1) : 0),
        cabinClass: realFlight.cabinClass || realFlight.flightClass?.name || realFlight.classInfo?.name || 'Economy',
        baggage: baggage,
        notes: realFlight.notes || '',
        segments: realFlight.segments ? realFlight.segments.map((s: any) => ({
            airline: s.airline?.name || s.airline || '',
            flightNumber: String(s.flightNo || s.flightNumber || ''),
            departureAirport: s.departure?.airport?.name || s.departure?.airport?.code || s.departure?.name || s.departureAirport || '',
            departureAirportCode: s.departure?.airport?.code || s.departure?.airport?.id || s.departureAirport || '',
            arrivalAirport: s.arrival?.airport?.name || s.arrival?.airport?.code || s.arrival?.name || s.arrivalAirport || '',
            arrivalAirportCode: s.arrival?.airport?.code || s.arrival?.airport?.id || s.arrivalAirport || '',
            departureTime: formatTime(s.departure?.date || s.departureTime || s.departure?.dateTime),
            arrivalTime: formatTime(s.arrival?.date || s.arrivalTime || s.arrival?.dateTime),
            duration: s.duration || '',
            stops: 0
        })) : []
    };
};

// Helper Functions
const getSafe = (obj: any, path: string, defaultValue: any = '') => { try { return path.split('.').reduce((acc, part) => acc && acc[part], obj) || defaultValue; } catch (e) { return defaultValue; } };

// Fallback B2C media base for image URLs when serviceAddress is empty (proxy mode)
const B2C_MEDIA_FALLBACK = 'https://b2cservice.lumitours.com';

const processApiImage = (rawUrl: string, serviceAddress: string, options: any = {}): string => {
    if (!rawUrl) return '';

    // If URL is already absolute, just return it
    if (rawUrl.toLowerCase().startsWith('http://') || rawUrl.toLowerCase().startsWith('https://')) {
        return rawUrl;
    }

    // Convert relative URL to absolute using media base
    let fullUrl = rawUrl;
    if (!rawUrl.startsWith('//')) {
        // Use serviceAddress if available, otherwise fall back to B2C CDN base
        const base = (serviceAddress || '').replace(/\/+$/, "") || B2C_MEDIA_FALLBACK;
        let mediaBase = base;
        if (base.toLowerCase().endsWith('/v2')) mediaBase = base.substring(0, base.length - 3);
        mediaBase += '/media';

        // Ensure lowercase as per Tourvisio docs
        const cleanPath = rawUrl.toLowerCase().replace(/^[~/]+/, '');
        fullUrl = `${mediaBase}/${cleanPath}`;
    }

    return fullUrl;
};

// Get optimized image URL through backend proxy (uses Tourvisio ImgProc API)
export const getOptimizedImageUrl = (rawUrl: string, options: {
    width?: number;
    height?: number;
    mode?: 'rmax' | 'rpad' | 'rcrop';
} = {}): string => {
    if (!rawUrl) return '';

    const { width = 800, height = 600, mode = 'rmax' } = options;
    const encodedUrl = encodeURIComponent(rawUrl);
    return `/api/tourvisio/image-proxy?url=${encodedUrl}&w=${width}&h=${height}&m=${mode}`;
};

const mapApiFlightData = (transData: any, serviceAddress: string): FlightSegment | undefined => {
    if (!transData) return undefined;

    let segmentList: any[] = [];
    let providerName = 'Flight';
    let flightNumber = '';
    let logoUrl = '';

    if (transData.items && Array.isArray(transData.items) && transData.items.length > 0) {
        const selectedItem = transData.items[0];
        if (selectedItem.segments && Array.isArray(selectedItem.segments)) {
            segmentList = selectedItem.segments;
        } else if (selectedItem.legs && Array.isArray(selectedItem.legs)) {
            segmentList = selectedItem.legs;
        }
        const rawItemName = selectedItem.airline?.name || selectedItem.provider || '';
        const itemAirlineCode = selectedItem.airline?.code || (selectedItem.flightNo ? String(selectedItem.flightNo).replace(/[^A-Za-z]/g, '').substring(0, 2) : '');
        providerName = resolveAirlineName(rawItemName, itemAirlineCode, selectedItem.flightNo);
        flightNumber = selectedItem.flightNo || selectedItem.flightNumber || '';
        logoUrl = selectedItem.airline?.logo || selectedItem.airline?.thumbnail || '';
    } else if (transData.segments && Array.isArray(transData.segments)) {
        segmentList = transData.segments;
    } else if (transData.legs && Array.isArray(transData.legs)) {
        segmentList = transData.legs;
    } else if (transData.departure && transData.arrival) {
        segmentList = [transData];
    }

    if (segmentList.length === 0) return undefined;

    const firstSeg = segmentList[0];
    const lastSeg = segmentList[segmentList.length - 1];

    if (!providerName) {
        const segAirlineCode = firstSeg.airline?.code || (firstSeg.flightNo ? String(firstSeg.flightNo).replace(/[^A-Za-z]/g, '').substring(0, 2) : '');
        providerName = resolveAirlineName(firstSeg.airline?.name, segAirlineCode, firstSeg.flightNo);
    }
    if (!flightNumber) flightNumber = firstSeg.flightNo || firstSeg.flightNumber || '';
    if (!logoUrl) logoUrl = firstSeg.airline?.logo || firstSeg.airline?.thumbnail || '';

    if (logoUrl) logoUrl = processApiImage(logoUrl, serviceAddress);

    const dep = firstSeg.departure;
    const arr = lastSeg.arrival;

    if (!dep || !arr) return undefined;

    let durationStr = '--';
    try {
        const diffMs = new Date(arr.date).getTime() - new Date(dep.date).getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const h = Math.floor(diffMins / 60);
        const m = Math.floor(diffMins % 60);
        durationStr = `${h}h ${m}m`;
    } catch (e) { }

    const formatTime = (isoStr: string | undefined) => {
        if (!isoStr) return '';
        try {
            const d = new Date(isoStr);
            if (isNaN(d.getTime())) {
                const match = isoStr.match(/(\d{2}:\d{2})/);
                return match ? match[1] : isoStr;
            }
            return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
        } catch { return isoStr; }
    };

    return {
        airline: providerName,
        flightNumber: flightNumber,
        departureAirport: depAirport,
        arrivalAirport: arrAirport,
        departureTime: formatTime(dep.date || dep.dateTime || dep.time || firstSeg?.departureTime),
        arrivalTime: formatTime(arr.date || arr.dateTime || arr.time || lastSeg?.arrivalTime),
        duration: durationStr,
        stops: segmentList.length - 1,
        logoUrl: logoUrl
    };
}

let sessionToken: string | null = null;
let tokenExpiry: Date | null = null;

import { STORAGE_KEY, DEFAULT_SETTINGS } from '../src/config/apiConfig';

export const getApiSettings = (): ApiSettings => {
    const settingsStr = localStorage.getItem(STORAGE_KEY);
    if (!settingsStr) {
        return DEFAULT_SETTINGS;
    }
    const settings = JSON.parse(settingsStr) as ApiSettings;
    // Force reset if localhost is found in legacy data
    if (settings.serviceAddress && settings.serviceAddress.includes('localhost')) {
        return DEFAULT_SETTINGS;
    }
    return settings;
};

const isTokenExpired = (): boolean => {
    return !tokenExpiry || new Date() >= tokenExpiry;
};

const makeApiRequest = async (endpoint: string, requestBody: object, logApiCall: (log: ApiLogEntry) => void, retryCount = 0, signal?: AbortSignal): Promise<any> => {
    const settings = getApiSettings();
    const startTime = Date.now();

    if ((!sessionToken || isTokenExpired()) && !endpoint.includes('authenticationservice/login')) {
        try {
            await getAuthToken(settings, logApiCall);
        } catch (authError) {
            const message = authError instanceof Error ? authError.message : String(authError);
            if (retryCount === 0) console.error(`Authentication failed: ${message}`);
            throw new Error(`Authentication failed: ${message}`);
        }
    }

    const baseUrl = (settings.serviceAddress || '').replace(/\/+$/, "");
    const urlPath = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const fullEndpoint = `${baseUrl}${urlPath}`;

    const headers = {
        'Content-Type': 'application/json; charset=utf-8',
        'Authorization': `Bearer ${sessionToken}`
    };

    let responseText = '';
    let status: 'success' | 'error' = 'error';
    let responseJson: any = null;

    // Enhanced Tourvisio API Logging
    console.group(`🌐 [TOURVISIO] ${endpoint}`);
    console.log('📤 Request Body:', JSON.stringify(requestBody, null, 2));

    try {
        const response = await fetch(fullEndpoint, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(requestBody),
            signal: signal,
        });

        const text = await response.text();
        responseText = text;

        try {
            responseJson = JSON.parse(text);
            responseText = JSON.stringify(responseJson, null, 2);
        } catch (e) {
            console.error('❌ Invalid JSON Response:', text.substring(0, 500));
            console.groupEnd();
            throw new Error(`Server returned invalid JSON. Response start: ${text.substring(0, 100)}...`);
        }

        // Log full response
        const duration = Date.now() - startTime;
        console.log('📥 Response:', responseJson);
        console.log(`⏱️ Duration: ${duration}ms`);
        console.log(`📊 Status: ${response.status} ${response.ok ? '✅' : '❌'}`);

        if (!response.ok || !responseJson.header?.success) {
            if (response.status === 401 && retryCount < 1) {
                console.log("🔄 Token expired (401), re-authenticating...");
                console.groupEnd();
                sessionToken = null;
                await getAuthToken(settings, logApiCall);
                return makeApiRequest(endpoint, requestBody, logApiCall, retryCount + 1);
            }

            const messages = responseJson?.header?.messages || [];
            const firstMsg = messages[0] || {};
            const errorMessage = firstMsg.message || 'An unknown API error occurred.';
            const provider = firstMsg.provider;
            const code = firstMsg.code;

            const isSoftError =
                code === 'ProductPriceNotFound' ||
                code === 'PriceNotFound' ||
                errorMessage.includes('not found') ||
                errorMessage.includes('availability');

            const fullError = provider ? `${errorMessage} (Provider: ${provider})` : errorMessage;
            console.error('❌ API Error:', fullError);
            console.groupEnd();

            const error = new Error(fullError);
            (error as any).isSoftError = isSoftError;
            throw error;
        }

        status = 'success';
        console.groupEnd();
        return responseJson;

    } catch (error: any) {
        const isAbort = error?.name === 'AbortError' || signal?.aborted || (error?.message && String(error.message).toLowerCase().includes('abort'));
        if (isAbort) {
            console.log(`🛑 API Call Aborted intentionally: ${endpoint}`);
            console.groupEnd();
            throw error;
        }
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        if (!responseText) responseText = errorMessage;
        console.error('❌ Request Failed:', errorMessage);
        console.groupEnd();
        throw error;
    } finally {
        logApiCall({
            timestamp: new Date().toISOString(),
            endpoint,
            request: requestBody,
            response: responseText,
            status,
        });
    }
}


export const getAuthToken = async (settings: ApiSettings, logApiCall: (log: ApiLogEntry) => void): Promise<string> => {
    const endpoint = `/api/authenticationservice/login`;
    const baseUrl = settings.serviceAddress.replace(/\/+$/, "");
    const fullEndpoint = `${baseUrl}${endpoint}`;

    const requestBody = {
        Agency: settings.agency,
        User: settings.user,
        Password: settings.password,
    };

    let responseText = '';
    let status: 'success' | 'error' = 'error';

    try {
        const response = await fetch(fullEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
        });

        const text = await response.text();
        responseText = text;

        let responseJson;
        try {
            responseJson = JSON.parse(text);
            responseText = JSON.stringify(responseJson, null, 2);
        } catch (e) {
            throw new Error(`Auth endpoint returned invalid JSON: ${text.substring(0, 100)}`);
        }

        if (!response.ok || !responseJson.header?.success || !responseJson.body?.token) {
            const errorMessage = responseJson?.header?.messages?.[0]?.message || 'Authentication failed.';
            throw new Error(errorMessage);
        }

        status = 'success';
        sessionToken = responseJson.body.token;
        tokenExpiry = new Date(responseJson.body.expiresOn);

        const currentSettings = getApiSettings();
        localStorage.setItem('tourvisioApiSettings', JSON.stringify({ ...currentSettings, token: sessionToken }));

        return sessionToken || '';

    } catch (error) {
        console.error('Authentication failed:', error);
        throw error;
    } finally {
        logApiCall({
            timestamp: new Date().toISOString(),
            endpoint,
            request: requestBody,
            response: responseText,
            status,
        });
    }
};

export const fetchHotelsByDestination = async (destinationId: string, logApiCall: (log: ApiLogEntry) => void): Promise<Hotel[]> => {
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + 60);
    const checkIn = futureDate.toISOString().split('T')[0];

    const query: SearchQuery = {
        type: SearchType.OnlyHotel,
        destinationId: destinationId,
        destinationType: 2,
        departureId: '10',
        checkIn: checkIn,
        nights: 7,
        rooms: 1,
        nationality: 'SE',
        currency: 'DKK',
        adults: 2,
        children: 0,
        childrenAges: []
    };

    try {
        const results = await searchTourvisio(query, 'en', logApiCall);
        return results.filter(r => 'pricePerNight' in r) as Hotel[];
    } catch (e) {
        console.error("Error fetching hotels by destination:", e);
        return [];
    }
};

/**
 * Fetch holiday packages (flights + hotel) for a destination.
 * Uses ProductType 1 (Holiday Package) via priceSearch.
 * Searches from Stockholm, 7 nights, 2 adults.
 */
export const fetchTourPackages = async (destinationId: string, language: Language, checkInDate: string, logApiCall: (log: ApiLogEntry) => void, currency: string = 'SEK'): Promise<HolidayPackage[]> => {
    const query: SearchQuery = {
        type: SearchType.PackageTours,
        destinationId: destinationId,
        destinationType: 2,
        departureId: '10', // Stockholm
        checkIn: checkInDate,
        nights: 7,
        rooms: 1,
        nationality: 'SE',
        currency: currency,
        adults: 2,
        children: 0,
        childrenAges: []
    };

    try {
        const results = await searchTourvisio(query, language, logApiCall);
        // Filter to holiday packages and sort by total price ascending
        const packages = results.filter(r => 'totalPrice' in r) as HolidayPackage[];
        packages.sort((a, b) => (a.totalPrice || 99999) - (b.totalPrice || 99999));
        return packages.slice(0, 6);
    } catch (e) {
        console.error("Error fetching tour packages:", e);
        return [];
    }
};

/**
 * Fetch premium (5-star or highest-rated) hotel-only options for a destination.
 * Uses ProductType 2 (Hotel Only) and filters by star rating.
 * Sliding window: tries multiple check-in dates (+14d, +21d, +28d, +35d) with min 3 nights.
 */
export const fetchPremiumHotels = async (destinationId: string, logApiCall: (log: ApiLogEntry) => void, currency: string = 'SEK'): Promise<Hotel[]> => {
    const SLIDE_DAYS = 7;
    const MAX_ATTEMPTS = 4;
    const START_OFFSET = 14; // days from now
    const MIN_NIGHTS = 3;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        try {
            const futureDate = new Date();
            futureDate.setDate(futureDate.getDate() + START_OFFSET + (attempt * SLIDE_DAYS));
            const checkIn = futureDate.toISOString().split('T')[0];

            console.log(`[PremiumHotels] Attempt ${attempt + 1}/${MAX_ATTEMPTS}: checkIn=${checkIn}, nights=${MIN_NIGHTS}`);

            const query: SearchQuery = {
                type: SearchType.OnlyHotel,
                destinationId: destinationId,
                destinationType: 2,
                departureId: '10',
                checkIn: checkIn,
                nights: MIN_NIGHTS,
                rooms: 1,
                nationality: 'SE',
                currency: currency,
                adults: 2,
                children: 0,
                childrenAges: []
            };

            const results = await searchTourvisio(query, 'en', logApiCall);
            const hotels = results.filter(r => 'pricePerNight' in r) as Hotel[];

            if (hotels.length > 0) {
                console.log(`[PremiumHotels] ✅ Attempt ${attempt + 1}: Found ${hotels.length} hotels for checkIn=${checkIn}`);

                // Try 5-star first
                let premium = hotels.filter(h => h.rating >= 5);
                // Fallback to 4+ star if no 5-star
                if (premium.length === 0) {
                    premium = hotels.filter(h => h.rating >= 4);
                }
                // Fallback to top-rated if still empty
                if (premium.length === 0) {
                    premium = [...hotels].sort((a, b) => (b.rating || 0) - (a.rating || 0));
                }

                // Sort by price ascending and take top 6
                premium.sort((a, b) => (a.pricePerNight || 99999) - (b.pricePerNight || 99999));
                return premium.slice(0, 6);
            }

            console.log(`[PremiumHotels] Attempt ${attempt + 1}: No hotels for checkIn=${checkIn}, sliding +${SLIDE_DAYS} days...`);
        } catch (e) {
            console.error(`[PremiumHotels] Attempt ${attempt + 1} error:`, e);
            // Continue to next attempt on error
        }
    }

    console.log('[PremiumHotels] All attempts exhausted, no hotels found');
    return [];
};

export const getDepartures = async (productType: number, culture: string, logApiCall: (log: ApiLogEntry) => void): Promise<LocationOption[]> => {
    try {
        const actualType = productType === 2 ? 1 : productType;
        const requestBody: any = { ProductType: actualType, Culture: culture, currency: 'DKK' };
        const response = await makeApiRequest('/api/productservice/getdepartures', requestBody, logApiCall);
        return response.body.locations.map((loc: any) => ({ id: loc.id, name: loc.name, type: loc.type, parentId: loc.parentId }));
    } catch (error) { return []; }
};

export const getArrivals = async (productType: number, departureLocations: { Id: string, Type: number }[], culture: string, logApiCall: (log: ApiLogEntry) => void): Promise<LocationOption[]> => {
    try {
        const actualType = productType === 2 ? 1 : productType;
        const requestBody: any = { ProductType: actualType, DepartureLocations: departureLocations, Culture: culture, currency: 'DKK' };
        const response = await makeApiRequest('/api/productservice/getarrivals', requestBody, logApiCall);
        return response.body.locations.map((loc: any) => ({ id: loc.id, name: loc.name, type: loc.type, parentId: loc.parentId }));
    } catch (error) { return []; }
};

export const getCheckInDates = async (productType: number, departureLocations: { Id: string, Type: number }[], arrivalLocations: { Id: string, Type: number }[], culture: string, logApiCall: (log: ApiLogEntry) => void): Promise<DateOption[]> => {
    try {
        const actualType = productType === 2 ? 1 : productType;
        const response = await makeApiRequest('/api/productservice/getcheckindates', { ProductType: actualType, DepartureLocations: departureLocations, ArrivalLocations: arrivalLocations, IncludeSubLocations: true, Culture: culture, currency: 'DKK' }, logApiCall);
        return response.body.dates.map((d: string) => ({ date: d }));
    } catch (error) { return []; }
};

export const getNights = async (productType: number, departureLocations: { Id: string, Type: number }[], arrivalLocations: { Id: string, Type: number }[], checkIn: string, culture: string, logApiCall: (log: ApiLogEntry) => void): Promise<NightOption[]> => {
    try {
        const actualType = productType === 2 ? 1 : productType;
        const response = await makeApiRequest('/api/productservice/getnights', { ProductType: actualType, DepartureLocations: departureLocations, ArrivalLocations: arrivalLocations, IncludeSubLocations: true, CheckIn: checkIn, Culture: culture, currency: 'DKK' }, logApiCall);
        return response.body.nights.map((n: number) => ({ night: n }));
    } catch (error) { return []; }
};

export const getDepartureAutoComplete = async (query: string, productType: number, culture: string, logApiCall: (log: ApiLogEntry) => void): Promise<any[]> => {
    try {
        // ProductType 6 = TourCulturePackage per docs sample, but we generalize
        const requestBody = {
            ProductType: productType,
            Culture: culture,
            Currency: 'DKK',
            Query: query,
            IncludeSubLocations: true
        };
        const response = await makeApiRequest('/api/productservice/getdepartureautocomplete', requestBody, logApiCall);
        return response.body.items || [];
    } catch (error) { return []; }
};

export const getArrivalAutoComplete = async (query: string, productType: number, culture: string, logApiCall: (log: ApiLogEntry) => void): Promise<any[]> => {
    try {
        const requestBody = {
            ProductType: productType,
            Culture: culture,
            Currency: 'DKK',
            Query: query, // Note: SearchApi typically requires a query text
            IncludeSubLocations: true
        };
        // Per documentation sample: /api/productservice/getarrivalautocomplete
        const response = await makeApiRequest('/api/productservice/getarrivalautocomplete', requestBody, logApiCall);
        return response.body.items || [];
    } catch (error) { return []; }
};

// processApiImage removed (duplicate)
// getMediaBaseUrl removed (not needed, logic is in processApiImage)

export const getHeroImageUrl = (imageUrl: string): string => {
    return processApiImage(imageUrl, getApiSettings().serviceAddress, { mode: 'max', width: 1920, quality: 90 });
};

export const getProductInfo = async (productType: number, productId: string, provider: number, culture: string = 'en-US', logApiCall: (log: ApiLogEntry) => void, currency: string = 'DKK'): Promise<ProductInfo | null> => {
    const settings = getApiSettings();
    const serviceAddress = settings.serviceAddress;

    // Normalize provider: 0 is invalid for Tourvisio's pSource field — default to SanTSG (1)
    const normalizedProvider = provider && provider > 0 ? provider : 1;

    // Try providers in order: normalized first, then fallbacks
    const providersToTry = [normalizedProvider, ...([1, 2, 3, 4].filter(p => p !== normalizedProvider))];

    const tryWithProvider = async (tryProvider: number): Promise<ProductInfo | null> => {
        const requestBody: any = {
            ProductType: productType,
            Product: productId,
            OwnerProvider: tryProvider,
            Culture: culture,
            Currency: currency
        };
        try {
            const response = await makeApiRequest('/api/productservice/getproductinfo', requestBody, logApiCall);
            const body = response.body;
            if (!body || !body.hotel) return null;
            const product = body.hotel;
            let bestSeason = product.seasons?.[0];
            let maxMedia = 0;
            if (product.seasons) {
                product.seasons.forEach((season: any) => {
                    const mediaCount = season.mediaFiles?.length || 0;
                    if (mediaCount > maxMedia) { maxMedia = mediaCount; bestSeason = season; }
                });
            }
            let images: string[] = [];
            if (bestSeason?.mediaFiles) {
                images = bestSeason.mediaFiles.filter((m: any) => m.fileType === 0 || m.fileType === 1).map((m: any) => processApiImage(m.url, serviceAddress, { width: 1024, mode: 'max' }));
            }
            if (images.length === 0 && product.thumbnail) {
                images.push(processApiImage(product.thumbnail, serviceAddress, { width: 1024, mode: 'max' }));
            }
            const facilities = bestSeason?.facilityCategories ? bestSeason.facilityCategories.flatMap((c: any) => c.facilities ? c.facilities.map((f: any) => f.name) : []) : [];
            let description = '';
            if (bestSeason?.textCategories) {
                const priorityCodes = ['DETAIL1', 'DETAIL', 'SEARCH', 'GENERAL', 'LOCATION', 'HOTEL_DESCRIPTION'];
                for (const code of priorityCodes) {
                    const category = bestSeason.textCategories.find((tc: any) => tc.code === code || tc.name?.toUpperCase() === code);
                    if (category?.presentations?.[0]?.text) { description = category.presentations[0].text; break; }
                }
                if (!description && bestSeason.textCategories.length > 0) {
                    const firstCat = bestSeason.textCategories[0];
                    if (firstCat && firstCat.presentations?.[0]?.text) { description = firstCat.presentations[0].text; }
                }
            }
            return {
                description: description || product.name || 'No description available.',
                images: images,
                facilities: facilities,
                coordinates: product.geolocation ? { latitude: product.geolocation.latitude, longitude: product.geolocation.longitude } : undefined,
                name: product.name,
                stars: product.stars ? Number(product.stars) : 0,
                rating: product.rating || 0,
                location: product.location || undefined,
                city: product.city || product.address?.city || undefined,
                country: product.country || undefined,
                thumbnailFull: product.thumbnailFull || '',
                provider: product.provider || tryProvider
            };
        } catch (err: any) {
            const msg = (err?.message || '').toLowerCase();
            // If pSource/provider error → try next provider
            if (msg.includes('psource') || msg.includes('parameter can not be null') || msg.includes('not implemented for')) {
                return null; // signal to try next
            }
            return null; // other errors also return null
        }
    };

    for (const p of providersToTry) {
        const result = await tryWithProvider(p);
        if (result) {
            if (p !== normalizedProvider) {
                console.log(`[getProductInfo] ✅ Succeeded with provider ${p} (original: ${provider})`);
            }
            return result;
        }
    }
    return null;
};

// getSafe removed (duplicate)

// Helper function to map API offer to internal result format
// Corrupted block removed

/**
 * Map flight segment from getOfferDetails API structure
 * Based on lumitours.com implementation
 */
export const mapApiFlightSegment = (transData: any, serviceAddress: string): FlightSegment | undefined => {
    if (!transData) return undefined;

    let segmentList: any[] = [];
    let providerName = 'Flight';
    let flightNumber = '';
    let logoUrl = '';

    // 1. Handle nested 'items' (PDF structure: flights -> items)
    // Often getting a container object, checking for 'items' array inside
    if (transData.items && Array.isArray(transData.items) && transData.items.length > 0) {
        // Usually index 0 is the selected flight option in that group
        const selectedItem = transData.items[0];

        if (selectedItem.segments && Array.isArray(selectedItem.segments)) {
            segmentList = selectedItem.segments;
        } else if (selectedItem.legs && Array.isArray(selectedItem.legs)) {
            segmentList = selectedItem.legs;
        }

        // Extract basic info from the item if available at top level
        const rawTopName = selectedItem.airline?.name || selectedItem.provider || '';
        const topCode = selectedItem.airline?.code || (selectedItem.flightNo ? String(selectedItem.flightNo).replace(/[^A-Za-z]/g, '').substring(0, 2) : '');
        providerName = resolveAirlineName(rawTopName, topCode, selectedItem.flightNo);
        flightNumber = selectedItem.flightNo || selectedItem.flightNumber || '';
        logoUrl = selectedItem.airline?.logo || selectedItem.airline?.thumbnail || '';
    }
    // 2. Handle direct 'segments' or 'legs' array
    else if (transData.segments && Array.isArray(transData.segments)) {
        segmentList = transData.segments;
    } else if (transData.legs && Array.isArray(transData.legs)) {
        segmentList = transData.legs;
    }
    // 3. Flat object (single segment)
    else if (transData.departure && transData.arrival) {
        segmentList = [transData];
    }

    if (segmentList.length === 0) return undefined;

    const firstSeg = segmentList[0];
    const lastSeg = segmentList[segmentList.length - 1];

    // Fill in missing details from segments if not found in parent
    // Helper to safely extract airline name (handles both string and object)
    const getAirlineStr = (airlineData: any, segFlightNo?: string): string => {
        const raw = (airlineData && typeof airlineData === 'object' && airlineData.name) ? airlineData.name : (typeof airlineData === 'string' ? airlineData : '');
        const code = (airlineData && typeof airlineData === 'object' && airlineData.code) ? airlineData.code : (segFlightNo ? String(segFlightNo).replace(/[^A-Za-z]/g, '').substring(0, 2) : '');
        return resolveAirlineName(raw, code, segFlightNo);
    };
    const getAirlineLogo = (airlineData: any): string => {
        if (!airlineData || typeof airlineData !== 'object') return '';
        return airlineData.logo || airlineData.thumbnail || airlineData.thumbnailFull || '';
    };

    if (!flightNumber) flightNumber = firstSeg.flightNo || firstSeg.flightNumber || '';
    if (!providerName) providerName = getAirlineStr(firstSeg.airline, flightNumber);
    if (!logoUrl) logoUrl = getAirlineLogo(firstSeg.airline);

    // Fix logo URL
    if (logoUrl) {
        logoUrl = processApiImage(logoUrl, serviceAddress);
    }

    const dep = firstSeg.departure;
    const arr = lastSeg.arrival;

    if (!dep || !arr) return undefined;

    // Calculate total duration (sum of segments + waiting times if available, or diff)
    let totalDurationVal = 0;

    // Attempt to parse duration from segments
    totalDurationVal = segmentList.reduce((acc: number, curr: any) => acc + (Number(curr.duration) || 0), 0);

    // If multiple segments, calculate layovers (approximation)
    if (segmentList.length > 1) {
        for (let i = 0; i < segmentList.length - 1; i++) {
            const segEnd = new Date(segmentList[i].arrival.date).getTime();
            const segNextStart = new Date(segmentList[i + 1].departure.date).getTime();
            const wait = (segNextStart - segEnd) / (1000 * 60); // minutes
            if (wait > 0) totalDurationVal += wait;
        }
    }

    // Format Duration from Minutes to Xh Ym
    let durationStr = '';
    if (totalDurationVal > 0) {
        const h = Math.floor(totalDurationVal / 60);
        const m = Math.floor(totalDurationVal % 60);
        durationStr = `${h}h ${m}m`;
    } else {
        // Fallback: Diff dates
        try {
            const diffMs = new Date(arr.date).getTime() - new Date(dep.date).getTime();
            const diffMins = Math.floor(diffMs / 60000);
            const h = Math.floor(diffMins / 60);
            const m = Math.floor(diffMins % 60);
            durationStr = `${h}h ${m}m`;
        } catch (e) { durationStr = "--"; }
    }

    const formatTime = (isoStr: string | undefined) => {
        if (!isoStr) return '';
        try {
            const d = new Date(isoStr);
            if (isNaN(d.getTime())) {
                const match = isoStr.match(/(\d{2}:\d{2})/);
                return match ? match[1] : isoStr;
            }
            return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
        } catch { return isoStr; }
    };

    return {
        airline: providerName,
        flightNumber: String(flightNumber || ''),
        departureAirport: depAirport,
        arrivalAirport: arrAirport,
        departureTime: formatTime(dep.date || dep.dateTime || dep.time || firstSeg?.departureTime),
        arrivalTime: formatTime(arr.date || arr.dateTime || arr.time || lastSeg?.arrivalTime),
        duration: durationStr,
        stops: segmentList.length - 1,
        logoUrl: logoUrl
    };
}

const mapJsonOfferToResult = (hotelData: any, queryType: SearchType, actualProductType: number, serviceAddress: string): HolidayPackage | Hotel | Tour => {
    const id = hotelData.id || `hotel-${Math.random()}`;
    const productId = hotelData.id;
    const provider = hotelData.provider || 0;
    const name = hotelData.name;
    const locationObj = { country: getSafe(hotelData, 'country.name'), city: getSafe(hotelData, 'city.name') };
    const rating = getSafe(hotelData, 'stars', 0);
    let rawThumb = hotelData.thumbnail || hotelData.Thumbnail || '';
    if (!rawThumb && hotelData.mediaFiles && hotelData.mediaFiles.length > 0) { rawThumb = hotelData.mediaFiles[0].url; }
    // Use backend imgproc-optimized URLs when available (thumbnailFull = 1920x1080, thumbnailMedium = 800x600)
    let imageUrl = hotelData.thumbnailFull || hotelData.thumbnailMedium || processApiImage(rawThumb, serviceAddress);
    let thumbnailUrl = rawThumb || processApiImage(rawThumb, serviceAddress);

    // Attempt to extract Themes and Facilities directly from the List response if available
    let themes: string[] = [];
    if (hotelData.themes && Array.isArray(hotelData.themes)) {
        themes = hotelData.themes.map((t: any) => t.name || t);
    }

    let facilities: string[] = [];
    if (hotelData.facilities && Array.isArray(hotelData.facilities)) {
        facilities = hotelData.facilities.map((f: any) => f.name || f);
    } else if (hotelData.facilityCategories && Array.isArray(hotelData.facilityCategories)) {
        // Sometimes nested in categories
        facilities = hotelData.facilityCategories.flatMap((c: any) => c.facilities ? c.facilities.map((f: any) => f.name) : []);
    }

    // --- FALLBACK LOGIC FOR FILTERS ---
    if (themes.length === 0) {
        if (name.toLowerCase().includes('resort') || rating >= 5) themes.push('Luxury Hotel', 'Spa Hotel');
        if (name.toLowerCase().includes('beach') || name.toLowerCase().includes('sea')) themes.push('Beach Hotel');
        if (name.toLowerCase().includes('family') || rating >= 4) themes.push('Family Hotel');
        if (locationObj.city && ['Istanbul', 'London', 'Paris', 'Dubai'].includes(locationObj.city)) themes.push('City Hotel');
        if (rating <= 3) themes.push('Budget Hotel');
    }

    if (facilities.length === 0) {
        if (rating >= 3) facilities.push('WiFi', 'Restaurant', 'Bar', '24-hour reception');
        if (rating >= 4) facilities.push('Air Condition', 'Pool', 'Fitness Center', 'Room Service');
        if (rating >= 5) facilities.push('Spa', 'Kids Club', 'Beach Access', 'Conference Hall');
    }

    const allOffersRaw = hotelData.offers || [];

    const offers: Offer[] = allOffersRaw.map((o: any) => {
        let boardName = getSafe(o, 'rooms.0.boardName') || getSafe(o, 'board.name', 'N/A');
        boardName = boardName.replace(/\s*&\s*/g, ' & ');

        const offer: Offer = {
            id: o.offerId,
            name: name,
            board: boardName,
            roomType: getSafe(o, 'rooms.0.roomName') || getSafe(o, 'room.name', 'N/A'),
            price: getSafe(o, 'price.amount', 0),
            currency: getSafe(o, 'price.currency', 'DKK'),
            checkIn: o.checkIn,
            night: o.night,
            available: o.isAvailable
        };

        if (o.transportations && Array.isArray(o.transportations)) {
            let outbound = o.transportations.find((t: any) => t.direction === 0 || t.direction === 'Outbound');
            let inbound = o.transportations.find((t: any) => t.direction === 1 || t.direction === 'Inbound');

            if (!outbound && o.transportations.length > 0) outbound = o.transportations[0];
            if (!inbound && o.transportations.length > 1) inbound = o.transportations[1];

            if (outbound) offer.outboundFlight = mapFlightSegment(outbound, serviceAddress);
            if (inbound) offer.returnFlight = mapFlightSegment(inbound, serviceAddress);
        }

        // Fallback: check rooms[0].transportation (priceSearch API format)
        // Structure: { outbound: { code, departure: {name, id}, arrival: {name, id}, flightProvider: {displayName} }, return: {...} }
        if (!offer.outboundFlight) {
            const roomTransport = getSafe(o, 'rooms.0.transportation');
            if (roomTransport) {
                // priceSearch format: object with outbound/return keys
                const outData = roomTransport.outbound || roomTransport;
                const inData = roomTransport.return;

                if (outData && (outData.code || outData.departure)) {
                    const outAirlineCode = outData.airline?.code || (outData.code ? String(outData.code).replace(/[^A-Za-z]/g, '').substring(0, 2) : '');
                    offer.outboundFlight = {
                        airline: resolveAirlineName(outData.flightProvider?.displayName || outData.airline?.name, outAirlineCode, outData.code),
                        flightNumber: outData.code || outData.pnlName || '',
                        departureAirport: outData.departure?.id || outData.departure?.name || '',
                        arrivalAirport: outData.arrival?.id || outData.arrival?.name || '',
                        departureTime: '',  // Not available in priceSearch
                        arrivalTime: '',
                        duration: '',
                        stops: 0,
                        segments: []
                    };
                }
                if (inData && (inData.code || inData.departure)) {
                    const inAirlineCode = inData.airline?.code || (inData.code ? String(inData.code).replace(/[^A-Za-z]/g, '').substring(0, 2) : '');
                    offer.returnFlight = {
                        airline: resolveAirlineName(inData.flightProvider?.displayName || inData.airline?.name, inAirlineCode, inData.code),
                        flightNumber: inData.code || inData.pnlName || '',
                        departureAirport: inData.departure?.id || inData.departure?.name || '',
                        arrivalAirport: inData.arrival?.id || inData.arrival?.name || '',
                        departureTime: '',
                        arrivalTime: '',
                        duration: '',
                        stops: 0,
                        segments: []
                    };
                }
            }
        }

        return offer;
    });

    const bestOffer = allOffersRaw.length > 0 ? offers[0] : {} as any;
    const currency = bestOffer.currency || 'DKK';
    const price = bestOffer.price || 0;
    const board = bestOffer.board || 'N/A';
    const roomType = bestOffer.roomType || 'N/A';

    let dealDescription = 'Package Holiday';

    const pkg: HolidayPackage = { id, productId, provider, name, location: locationObj, rating, pricePerNight: price, currency, imageUrl, thumbnailUrl, board, roomType, themes: themes, facilities: facilities, totalPrice: price, dealDescription, offers };
    return pkg;
};

// Define return type interface
export interface SearchResponse {
    results: (HolidayPackage | Hotel | Tour)[];
    searchId: string;
    totalCount?: number;
}

export interface PriceBandDate {
    date: string;
    price: number;
    currency: string;
}

export const getAlternativeDatesFromCache = async (
    query: SearchQuery,
    daysAhead: number = 3,
    daysBehind: number = 3
): Promise<PriceBandDate[]> => {
    try {
        const queryParams = new URLSearchParams({
            departureId: query.departureId || '',
            arrivalId: query.destinationId || '',
            checkIn: query.checkIn,
            nights: String(query.nights || 7),
            adults: String(query.adults || 2),
            children: String(query.children || 0),
            daysAhead: String(daysAhead),
            daysBehind: String(daysBehind)
        });

        const response = await fetch(`/api/tour-cache/alternative-dates?${queryParams}`);
        if (!response.ok) return [];

        const data = await response.json();
        if (data.success && data.dates) {
            return data.dates;
        }
        return [];
    } catch (e) {
        console.error('Failed to get alternative dates from cache:', e);
        return [];
    }
};

export const searchTourvisio = async (query: SearchQuery, language: Language, logApiCall: (log: ApiLogEntry) => void): Promise<(HolidayPackage | Hotel | Tour)[]> => {

    const selectedCulture = language === 'en' ? 'en-US' : language === 'da' ? 'da-DK' : language === 'nb' ? 'nb-NO' : 'sv-SE';
    const settings = getApiSettings();
    const widgetSettings = await getWidgetSettings();
    const serviceAddress = settings.serviceAddress;

    // Branch by search type per TourVisio docs
    const isHotelOnly = query.type === SearchType.OnlyHotel;
    const actualProductType = isHotelOnly ? 2 : 1; // 2=Hotel Product, 1=Holiday Package

    const arrivalLocations = query.destinationId ? [{ Id: query.destinationId, Type: query.destinationType || 2 }] : [];

    const roomCriteria: any[] = [];
    const numRooms = Math.max(1, query.rooms || 1);
    const totalAdults = Math.max(1, query.adults);
    const totalChildren = query.children;
    const allChildAges = query.childrenAges || [];
    const baseAdults = Math.floor(totalAdults / numRooms);
    const remainderAdults = totalAdults % numRooms;

    for (let i = 0; i < numRooms; i++) {
        let roomAdults = baseAdults;
        if (i < remainderAdults) roomAdults++;
        const roomChildren: number[] = [];
        for (let j = 0; j < totalChildren; j++) {
            if (j % numRooms === i) {
                roomChildren.push(allChildAges[j] || 8);
            }
        }
        roomCriteria.push({ Adult: roomAdults, ChildAges: roomChildren });
    }

    const effectiveCurrency = query.currency || 'SEK';

    let requestBody: any;

    if (isHotelOnly) {
        // ─── HOTEL-ONLY (ProductType 2) ───
        // Per TourVisio Hotel Product docs: https://docs.santsg.com/tourvisio/hproduct/
        // No flight params: no DepartureLocations, ServiceTypes, GetTransportations, ShowOnlyNonStopFlight
        console.log('🏨 Hotel-Only search (ProductType 2)');
        requestBody = {
            productType: actualProductType,
            checkAllotment: true,
            checkStopSale: true,
            getOnlyDiscountedPrice: false,
            getOnlyBestOffers: true,
            arrivalLocations: arrivalLocations,
            Products: query.hotelId ? [String(query.hotelId)] : [],
            roomCriteria: roomCriteria,
            nationality: query.nationality || 'SE',
            checkIn: query.checkIn,
            night: query.nights,
            currency: effectiveCurrency,
            culture: selectedCulture,
        };
    } else {
        // ─── HOLIDAY PACKAGE (ProductType 1) ───
        // Existing flow with flight params
        const departureLocations = (query.departureId) ? [{ Id: query.departureId, Type: 2 }] : [];
        console.log('✈️🏨 Holiday Package search (ProductType 1)');
        requestBody = {
            ProductType: actualProductType,
            ServiceTypes: [1, 3], // 1=Charter, 3=Scheduled flights
            DepartureLocations: departureLocations,
            ArrivalLocations: arrivalLocations,
            IncludeSubLocations: true,
            CheckIn: `${query.checkIn}T00:00:00`,
            Night: query.nights,
            Products: [], // Workaround: Never send Products filter for PackageTours as Tourvisio API fails with 'Hotel price not found'. We filter locally.
            RoomCriteria: roomCriteria,
            CheckAllotment: false,
            CheckStopSale: false,
            Nationality: query.nationality || 'SE',
            Currency: effectiveCurrency,
            Culture: selectedCulture,
            GetTransportations: true,
            ShowOnlyNonStopFlight: false,
            pagingOption: {
                currentPage: 1,
                pageRowCount: 20,
                sort: 1
            }
        };
    }

    try {
        const response = await makeApiRequest('/api/productservice/pricesearch', requestBody, logApiCall);
        const body = response.body;

        if (body.searchId) {
            sessionStorage.setItem('tourvisio_search_id', body.searchId);
        }

        let rawResults: any[] = body.hotels || body.offers || [];

        // Whitelist Filtering
        if (widgetSettings.restrictToWhitelist) {
            const whitelist = await getWhitelist();
            const whitelistIds = new Set(whitelist.map(item => item.id));
            rawResults = rawResults.filter((item: any) => {
                const itemId = String(item.id || '');
                return whitelistIds.has(itemId);
            });
        }

        let mappedResults;
        if (rawResults.length > 0) {
            const searchType = isHotelOnly ? SearchType.OnlyHotel : SearchType.PackageTours;
            mappedResults = rawResults.map(item => mapJsonOfferToResult(item, searchType, actualProductType, serviceAddress));
            
            // If we are looking for a specific hotel, filter locally since we removed Products filter for API stability
            if (query.hotelId) {
                mappedResults = mappedResults.filter(item => String(item.id) === String(query.hotelId));
            }
            return mappedResults;
        }
    } catch (e) {
        console.error('Search failed', e);
    }
    return [];
};


/**
 * Get all offers for a specific hotel — used when getOnlyBestOffers was true in PriceSearch.
 * Per TourVisio Hotel Product docs: https://docs.santsg.com/tourvisio/hproduct/#GetOffers
 * Returns all room/board combinations with room info (images, facilities).
 */
export const getHotelOffers = async (
    searchId: string,
    offerId: string,
    productId: string,
    logApiCall: (log: ApiLogEntry) => void,
    currency: string = 'SEK',
    culture: string = 'en-US'
): Promise<any> => {
    console.log(`🏨 getHotelOffers: searchId=${searchId}, offerId=${offerId}, productId=${productId}`);

    const requestBody = {
        searchId: searchId,
        offerId: offerId,
        productType: 2,
        productId: String(productId),
        currency: currency,
        culture: culture,
        getRoomInfo: true, // Required for room presentations, facilities, and media files
    };

    try {
        const response = await makeApiRequest('/api/productservice/getoffers', requestBody, logApiCall);
        console.log('🏨 getHotelOffers response:', JSON.stringify(response).substring(0, 500));
        return response.body || response;
    } catch (e) {
        console.error('❌ getHotelOffers failed:', e);
        return null;
    }
};

// 1. GetPagingData Fonksiyonu (User Requested)
export const getPagingData = async (
    searchId: string,
    pageNumber: number,
    sortType: number = 1,
    logApiCall: (log: ApiLogEntry) => void
): Promise<SearchResponse> => {
    const requestBody = {
        searchId: searchId,
        pagingOption: {
            currentPage: pageNumber,
            pageRowCount: 20,
            sort: sortType,
            productType: 1 // Holiday Package
        },
        currency: 'SEK',
        culture: 'en-US'
    };

    // makeApiRequest mevcut yapısı kullanılarak çağrılır
    const response = await makeApiRequest('/api/productservice/getpagingdata', requestBody, logApiCall);

    if (!response || !response.body || !response.body.hotels) {
        // Check for offers as fallback
        if (response.body && response.body.offers) {
            // Handle offers if different structure
        } else {
            return { results: [], searchId: searchId, totalCount: 0 };
        }
    }

    const rawResults = response.body.hotels || response.body.offers || [];

    const mappedResults = rawResults.map((h: any) =>
        mapJsonOfferToResult(h, SearchType.PackageTours, 1, getApiSettings().serviceAddress)
    );

    return {
        results: mappedResults,
        searchId: searchId,
        totalCount: response.body.pagingData?.totalRowCount || mappedResults.length
    };
};

export const fetchNextPageWithFlights = async (searchId: string, page: number, pageSize: number = 20): Promise<SearchResponse> => {
    return getPagingData(searchId, page, 1, () => { });
};

/**
 * Fetches alternative flight options for a specific search results session
 */
export const getAlternativeFlights = async (searchId: string, options: { currency?: string, culture?: string } = {}): Promise<any[]> => {
    try {
        const { currency = 'SEK', culture = 'sv-SE' } = options;
        const response = await fetch(`${API_URL}/tourvisio/alternative-flights/${searchId}?currency=${currency}&culture=${culture}`);
        const data = await response.json();
        
        if (!data.success) {
            console.warn('⚠️ Failed to fetch alternative flights:', data.error);
            return [];
        }
        
        return data.flights || [];
    } catch (error) {
        console.error('❌ Error in getAlternativeFlights:', error);
        return [];
    }
};



/**
 * Backend cache endpoint kullanan arama fonksiyonu
 * Uçuş bilgileri dahil formatlanmış sonuç döndürür
 */
export interface SearchWithCacheResult {
    hotels: (HolidayPackage | Hotel | Tour)[];
    alternativeFlights?: RealFlightOption[];
    totalFlights?: number;
    searchId?: string;
}

export const searchWithCache = async (
    query: SearchQuery,
    language: Language,
    logApiCall: (log: ApiLogEntry) => void
): Promise<SearchWithCacheResult> => {
    try {
        // Backend cache endpoint'ini kullan
        // Derive culture from query or language
        const derivedCulture = query.culture ||
            (language === 'sv' ? 'sv-SE' : language === 'da' ? 'da-DK' : language === 'nb' ? 'nb-NO' : 'sv-SE');
        const response = await fetch('/api/tourvisio/search-cached', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                departureId: query.departureId,
                arrivalId: query.destinationId,
                checkIn: query.checkIn,
                nights: query.nights,
                adults: query.adults,
                children: query.children,
                childrenAges: query.childrenAges,
                currency: query.currency,
                culture: derivedCulture, // Dynamic: from URL > language > sv-SE default
                hotelId: query.hotelId,
                forceLive: (query as any).forceLive, // Pass forceLive to backend
                productType: 1 // Holiday Package (per tourvisioEnums.ts)
            })
        });

        // Check HTTP status first
        if (!response.ok) {
            console.warn(`Backend cache returned ${response.status}, falling back to direct API`);
            const fallbackHotels = await searchTourvisio(query, language, logApiCall);
            return { hotels: fallbackHotels };
        }

        const data = await response.json();

        if (!data.success) {
            console.warn('Backend cache search failed, falling back to direct API');
            const fallbackHotels = await searchTourvisio(query, language, logApiCall);
            return { hotels: fallbackHotels };
        }

        // Return full response including alternativeFlights
        return {
            hotels: data.hotels || data.data || [],
            alternativeFlights: (data.alternativeFlights || []).map((f: any) => ({
                id: f.id || f.offerId,
                offerId: f.offerId,
                airline: resolveAirlineName(f.airline, f.airlineCode, f.flightNumber),
                airlineCode: f.airlineCode || '',
                logoUrl: f.logoUrl || '',
                flightNumber: f.flightNumber || '',
                departureAirport: f.departureAirport || '',
                arrivalAirport: f.arrivalAirport || '',
                departureTime: f.departureTime || '',
                arrivalTime: f.arrivalTime || '',
                departureDate: f.departureDate || '',
                duration: f.duration || '',
                stops: f.stops || 0,
                price: f.price || 0,
                priceDifference: f.priceDifference || 0,
                currency: f.currency || 'DKK',
                isReturn: f.direction === 'return' || f.isReturn || false
            })),
            totalFlights: data.totalFlights || 0,
            searchId: data.searchId
        };

    } catch (e) {
        console.error("SearchWithCache failed:", e);
        const fallbackHotels = await searchTourvisio(query, language, logApiCall);
        return { hotels: fallbackHotels };
    }
}; // Backend'den gelen formatlanmış veriyi HolidayPackage tipine dönüştür

// ==========================================
// HELPER: Parse branded fare info from TourVisio flight data
// ==========================================
const SERVICE_GROUP_LABELS: Record<number, string> = {
    0: 'Branded Fare',
    1: 'Baggage',
    2: 'Frequent Flyer',
    3: 'In-flight Entertainment',
    4: 'Lounge Access',
    5: 'Meal & Beverage',
    6: 'Pets',
    7: 'Upgrades'
};

const parseBrandedFares = (flightGroup: any): BrandedFare[] => {
    const fares: BrandedFare[] = [];

    // TourVisio returns flightBrandInfo at group level or inside items
    const brandInfo = flightGroup.flightBrandInfo || flightGroup.items?.[0]?.flightBrandInfo;

    // Single brand info — extract as one fare option
    if (brandInfo?.name) {
        const features: BrandedFareFeature[] = [];

        // Parse features array if present
        if (brandInfo.features && Array.isArray(brandInfo.features)) {
            brandInfo.features.forEach((f: any) => {
                features.push({
                    serviceGroup: f.serviceGroup ?? 0,
                    label: f.commercialName || f.description || f.name || SERVICE_GROUP_LABELS[f.serviceGroup] || 'Feature',
                    pricingType: f.pricingType ?? 0
                });
            });
        }

        // Also check baggageInformations for baggage feature
        const baggage = flightGroup.baggageInformations?.[0] || flightGroup.items?.[0]?.baggageInformations?.[0];
        if (baggage && !features.some(f => f.serviceGroup === 1)) {
            let baggageLabel = '';
            if (baggage.weight && (baggage.unit || baggage.unitType !== undefined)) {
                const unitStr = baggage.unit || (baggage.unitType === 0 || baggage.unitType === '0' ? 'kg' : baggage.unitType === 2 || baggage.unitType === '2' ? 'Piece' : 'kg');
                baggageLabel = `${baggage.weight} ${unitStr}`;
            } else {
                baggageLabel = baggage.description || (baggage.weight ? `${baggage.weight} kg` : '');
            }
            if (baggageLabel) {
                features.push({ serviceGroup: 1, label: baggageLabel, pricingType: 0 });
            }
        }

        fares.push({
            name: brandInfo.name,
            priceDifference: brandInfo.priceDifference || 0,
            currency: brandInfo.currency || 'DKK',
            features
        });
    }

    // Check for multiple branded fares in flightClassInformations
    const classInfos = flightGroup.flightClassInformations || flightGroup.items?.[0]?.flightClassInformations || [];
    classInfos.forEach((classInfo: any) => {
        const classBrandInfo = classInfo.flightBrandInfo;
        if (classBrandInfo?.name && !fares.some(f => f.name === classBrandInfo.name)) {
            const features: BrandedFareFeature[] = [];
            if (classBrandInfo.features && Array.isArray(classBrandInfo.features)) {
                classBrandInfo.features.forEach((f: any) => {
                    features.push({
                        serviceGroup: f.serviceGroup ?? 0,
                        label: f.commercialName || f.description || f.name || SERVICE_GROUP_LABELS[f.serviceGroup] || 'Feature',
                        pricingType: f.pricingType ?? 0
                    });
                });
            }
            fares.push({
                name: classBrandInfo.name,
                priceDifference: classBrandInfo.priceDifference || classInfo.priceDifference || 0,
                currency: classBrandInfo.currency || classInfo.currency || 'DKK',
                features,
                offerId: classInfo.offerId || classInfo.id
            });
        }
    });

    if (fares.length > 0) {
        console.log(`🏷️ Parsed ${fares.length} branded fares:`, fares.map(f => `${f.name} (+${f.priceDifference})`).join(', '));
    }
    return fares;
};

export const getOfferDetails = async (offerId: string, logApiCall: (log: ApiLogEntry) => void, signal?: AbortSignal, culture: string = "en-US", currency: string = "EUR"): Promise<Offer | null> => {
    // CRITICAL: Capture offerId as a const to prevent any closure mutation
    const capturedOfferId = String(offerId);
    const requestNonce = `${capturedOfferId}_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
    console.log(`🔍 [${requestNonce}] getOfferDetails called for: ${capturedOfferId}`);

    const settings = getApiSettings();
    const serviceAddress = settings.serviceAddress;

    try {
        // Construct body inline — no intermediate variables that could be shared/mutated
        const requestBody = { offerIds: [capturedOfferId], getProductInfo: true, currency: currency, culture: culture };
        console.log(`📤 [${requestNonce}] Sending offerIds: [${capturedOfferId}]`);

        const response = await makeApiRequest(
            '/api/productservice/getofferdetails',
            requestBody,
            logApiCall,
            0,
            signal
        );
        const body = response.body;

        console.log(`📦 [${requestNonce}] getOfferDetails response for ${capturedOfferId}`);

        if (!body || !body.offerDetails || body.offerDetails.length === 0) {
            console.log(`❌ [${requestNonce}] No offerDetails in response`);
            return null;
        }

        const details = body.offerDetails[0];

        // AŞAMA 1: Post-response validation — verify API returned the correct offer
        const returnedOfferId = details.offerId || details.id;
        if (returnedOfferId && String(returnedOfferId) !== capturedOfferId) {
            console.error(`🚨 [${requestNonce}] OFFER ID MISMATCH! Requested: ${capturedOfferId}, Got: ${returnedOfferId}`);
            console.error(`🚨 This indicates the API returned data for a different offer.`);
        } else {
            console.log(`✅ [${requestNonce}] Offer ID verified: ${returnedOfferId || '(no offerId in response)'}`);
        }

        console.log(`✅ [${requestNonce}] Got offer details, has flights:`, !!details.flights, 'count:', details.flights?.length || 0);

        const result: Offer = {
            id: capturedOfferId,
            name: "",
            board: "",
            roomType: "",
            price: 0,
            currency: "EUR",
            checkIn: details.checkIn,
            night: 0,
            available: details.isAvailable
        };

        if (details.flights && Array.isArray(details.flights) && details.flights.length > 0) {
            console.log('✈️ Processing flights, count:', details.flights.length);

            // AŞAMA 2: Enhanced route detection
            // route=1 → Outbound (Gidiş), route=2 → Inbound (Dönüş)
            // Check 3 levels: flightGroup.route → items[0].route → flightClassInformations[0].route
            let outboundGroup: any = null;
            let returnGroup: any = null;

            for (const flightGroup of details.flights) {
                let groupRoute = flightGroup.route;

                // Level 2: items[0].route
                if (groupRoute === undefined || groupRoute === null) {
                    groupRoute = flightGroup.items?.[0]?.route;
                }

                // Level 3: flightClassInformations[0].route
                if (groupRoute === undefined || groupRoute === null) {
                    groupRoute = flightGroup.flightClassInformations?.[0]?.route;
                }

                console.log(`  ✈️ Flight group route: ${groupRoute} (group: ${flightGroup.route}, item: ${flightGroup.items?.[0]?.route}, classInfo: ${flightGroup.flightClassInformations?.[0]?.route})`);

                if (groupRoute === 1) {
                    outboundGroup = flightGroup;
                } else if (groupRoute === 2) {
                    returnGroup = flightGroup;
                }
            }

            // If no route found at any level, dump full structure for debugging, then use index-based
            if (!outboundGroup && !returnGroup) {
                console.warn('⚠️ No route field at any level. Full flight structure dump:');
                details.flights.forEach((fg: any, idx: number) => {
                    console.warn(`  Flight[${idx}] keys:`, Object.keys(fg));
                    if (fg.items?.[0]) {
                        console.warn(`  Flight[${idx}].items[0] keys:`, Object.keys(fg.items[0]));
                    }
                    if (fg.flightClassInformations?.[0]) {
                        console.warn(`  Flight[${idx}].flightClassInformations[0] keys:`, Object.keys(fg.flightClassInformations[0]));
                    }
                });
                console.warn('⚠️ Using index-based assignment: flights[0]=outbound, flights[1]=return');
                outboundGroup = details.flights[0];
                returnGroup = details.flights.length > 1 ? details.flights[1] : null;
            } else {
                console.log(`✅ Flight groups via route field: outbound=${!!outboundGroup}, return=${!!returnGroup}`);
            }

            if (outboundGroup?.items?.length > 0) {
                result.outboundFlight = mapFlightSegment(outboundGroup.items[0], serviceAddress);
                // Phase 2: Extract flightClassInformations, baggageInformations, notes
                const outClassInfo = outboundGroup.flightClassInformations?.[0] || outboundGroup.items?.[0]?.flightClassInformations?.[0];
                if (outClassInfo) {
                    result.outboundFlight.cabinClass = outClassInfo.classInfo || outClassInfo.name || outClassInfo.className || outClassInfo.class || '';
                    result.outboundFlight.baggage = outClassInfo.baggageAllowance || '';
                }
                const outBaggage = outboundGroup.baggageInformations?.[0] || outboundGroup.items?.[0]?.baggageInformations?.[0] || outboundGroup.items?.[0]?.segments?.[0]?.baggageInformations?.[0];
                if (outBaggage && !result.outboundFlight.baggage) {
                    // Concatenate weight + unit for display
                    if (outBaggage.weight && (outBaggage.unit || outBaggage.unitType !== undefined)) {
                        const unitStr = outBaggage.unit || (outBaggage.unitType === 0 || outBaggage.unitType === '0' ? 'kg' : outBaggage.unitType === 2 || outBaggage.unitType === '2' ? 'Piece' : 'kg');
                        result.outboundFlight.baggage = `${outBaggage.weight} ${unitStr}`;
                    } else {
                        result.outboundFlight.baggage = outBaggage.description || (outBaggage.weight ? `${outBaggage.weight} kg` : '');
                    }
                }
                // Final fallback: flightBrandInfo.name
                if (!result.outboundFlight.baggage) {
                    const brandInfo = outboundGroup.flightBrandInfo || outboundGroup.items?.[0]?.flightBrandInfo;
                    if (brandInfo?.name) result.outboundFlight.baggage = brandInfo.name;
                }
                if (outboundGroup.notes || outboundGroup.items?.[0]?.notes) {
                    result.outboundFlight.notes = outboundGroup.notes || outboundGroup.items[0].notes || '';
                }
                console.log('🛫 Outbound:', result.outboundFlight?.airline, result.outboundFlight?.departureAirport, '→', result.outboundFlight?.arrivalAirport, '| class:', result.outboundFlight?.cabinClass, '| baggage:', result.outboundFlight?.baggage);
                // Extract branded fares for outbound
                result.outboundBrandedFares = parseBrandedFares(outboundGroup);
            }

            if (returnGroup?.items?.length > 0) {
                result.returnFlight = mapFlightSegment(returnGroup.items[0], serviceAddress);
                // Phase 2: Extract flightClassInformations, baggageInformations, notes
                const retClassInfo = returnGroup.flightClassInformations?.[0] || returnGroup.items?.[0]?.flightClassInformations?.[0];
                if (retClassInfo) {
                    result.returnFlight.cabinClass = retClassInfo.classInfo || retClassInfo.name || retClassInfo.className || retClassInfo.class || '';
                    result.returnFlight.baggage = retClassInfo.baggageAllowance || '';
                }
                const retBaggage = returnGroup.baggageInformations?.[0] || returnGroup.items?.[0]?.baggageInformations?.[0] || returnGroup.items?.[0]?.segments?.[0]?.baggageInformations?.[0];
                if (retBaggage && !result.returnFlight.baggage) {
                    // Concatenate weight + unit for display
                    if (retBaggage.weight && (retBaggage.unit || retBaggage.unitType !== undefined)) {
                        const unitStr = retBaggage.unit || (retBaggage.unitType === 0 || retBaggage.unitType === '0' ? 'kg' : retBaggage.unitType === 2 || retBaggage.unitType === '2' ? 'Piece' : 'kg');
                        result.returnFlight.baggage = `${retBaggage.weight} ${unitStr}`;
                    } else {
                        result.returnFlight.baggage = retBaggage.description || (retBaggage.weight ? `${retBaggage.weight} kg` : '');
                    }
                }
                // Final fallback: flightBrandInfo.name
                if (!result.returnFlight.baggage) {
                    const brandInfo = returnGroup.flightBrandInfo || returnGroup.items?.[0]?.flightBrandInfo;
                    if (brandInfo?.name) result.returnFlight.baggage = brandInfo.name;
                }
                if (returnGroup.notes || returnGroup.items?.[0]?.notes) {
                    result.returnFlight.notes = returnGroup.notes || returnGroup.items[0].notes || '';
                }
                console.log('🛬 Return:', result.returnFlight?.airline, result.returnFlight?.departureAirport, '→', result.returnFlight?.arrivalAirport, '| class:', result.returnFlight?.cabinClass, '| baggage:', result.returnFlight?.baggage);
                // Extract branded fares for return
                result.returnBrandedFares = parseBrandedFares(returnGroup);
            }

        } else {
            console.log('⚠️ No flights in offer details');
        }

        // Phase 2: Also extract price from offerDetails
        if (details.price) {
            result.price = typeof details.price === 'object' ? details.price.amount : details.price;
            result.currency = typeof details.price === 'object' ? (details.price.currency || 'DKK') : 'DKK';
        }

        return result;
    } catch (error) {
        console.error(`Failed to get offer details for ${capturedOfferId}:`, error);
        return null;
    }
};

/**
 * DISABLED: LumiTours does not sell standalone flights.
 * Flights are only available within Holiday Packages (ProductType 1) via getofferdetails.
 * This function is kept as a stub to avoid breaking imports.
 */
export const searchFlights = async (query: FlightSearchQuery, language: Language, logApiCall: (log: ApiLogEntry) => void): Promise<FlightOffer[]> => {
    console.log('✈️ searchFlights DISABLED — standalone flight sales not available. Use Holiday Package search instead.');
    return [];
};

// Flight offer details for separate flight selection page
// Uses Holiday Package API (ProductType 1) with getTransportations flag
export interface FlightOfferOption {
    offerId: string;
    flightGroupId: string;
    direction: 'outbound' | 'return';
    airline: string;
    airlineCode: string;
    flightNumber: string;
    departureAirport: string;
    arrivalAirport: string;
    departureTime: string;
    arrivalTime: string;
    flightDate: string;
    duration: string;
    stops: number;
    logoUrl: string;
    priceDifference: number;
    currency: string;
    isSelected: boolean;
}

export interface FlightOffersResult {
    outboundFlights: FlightOfferOption[];
    returnFlights: FlightOfferOption[];
    basePrice: number;
    currency: string;
}

// Get flight offer options from Holiday Package offers (like lumitours.com)
export const getFlightOffers = async (
    offerId: string,
    departureId: string,
    arrivalId: string,
    checkIn: string,
    nights: number,
    adults: number,
    children: number,
    childrenAges: number[],
    logApiCall: (log: ApiLogEntry) => void,
    currency: string = 'DKK'
): Promise<FlightOffersResult> => {
    const settings = getApiSettings();
    const serviceAddress = settings.serviceAddress;

    console.log('âœˆï¸ getFlightOffers called:', { offerId, departureId, arrivalId, checkIn });

    const outboundFlights: FlightOfferOption[] = [];
    const returnFlights: FlightOfferOption[] = [];
    let basePrice = 0;

    // 1. Fetch the BASE flight details from the offerId (This guarantees we show the selected flight)
    if (offerId) {
        try {
            console.log('ğŸ“¦ Fetching baseline details for offer:', offerId);
            const baseDetails = await getOfferDetails(offerId, logApiCall);

            if (baseDetails) {
                if (!basePrice) basePrice = baseDetails.price;

                const mapSegToOption = (seg: FlightSegment, direction: 'outbound' | 'return'): FlightOfferOption => ({
                    offerId: offerId,
                    flightGroupId: `${direction}-default`, // Unique ID for default
                    direction: direction,
                    airline: resolveAirlineName(seg.airline, seg.airlineCode, seg.flightNumber),
                    airlineCode: seg.airlineCode || '', // Not strictly needed for UI if logo present
                    flightNumber: String(seg.flightNumber || ''),
                    departureAirport: String(seg.departureAirport || ''),
                    arrivalAirport: String(seg.arrivalAirport || ''),
                    departureTime: String(seg.departureTime || ''),
                    arrivalTime: String(seg.arrivalTime || ''),
                    flightDate: (typeof seg.departureTime === 'string' && seg.departureTime.includes(',')) ? seg.departureTime : checkIn, // Fallback date
                    duration: String(seg.duration || ''),
                    stops: seg.stops || 0,
                    logoUrl: seg.logoUrl || '',
                    priceDifference: 0,
                    currency: baseDetails.currency,
                    isSelected: true // Default is selected
                });

                if (baseDetails.outboundFlight) {
                    const opt = mapSegToOption(baseDetails.outboundFlight, 'outbound');
                    outboundFlights.push(opt);
                    console.log('âœ… Added baseline outbound flight:', opt.flightNumber);
                }
                if (baseDetails.returnFlight) {
                    const opt = mapSegToOption(baseDetails.returnFlight, 'return');
                    returnFlights.push(opt);
                    console.log('âœ… Added baseline return flight:', opt.flightNumber);
                }
            }
        } catch (err) {
            console.warn('âš ï¸ Failed to fetch baseline offer details:', err);
        }
    }

    // 2. Search for ALTERNATIVE hybrid flights using Holiday Package
    // We run this to give user more options. If it fails, we still have the baseline.
    const requestBody = {
        ProductType: 1, // Holiday Package
        DepartureLocations: departureId ? [{ Id: departureId, Type: 2 }] : [],
        ArrivalLocations: [{ Id: arrivalId, Type: 2 }],
        CheckIn: `${checkIn}T00:00:00`,
        Night: nights,
        RoomCriteria: [{ Adult: adults, ChildAges: childrenAges }],
        Nationality: 'SE',
        Culture: 'en-US',
        Currency: currency || 'DKK',
        // Critical parameters for paging support
        pagingOptions: [
            {
                currentPage: 1,
                pageRowCount: 20,
                productType: 1,
                getFilters: true,
                filters: [],
                getPagingCount: true,
                isNewPagingRequest: true
            }
        ],
        getTransportations: true,
        forceFlightBundlePackage: false,
        searchBrandedFares: true,
        acceptPendingProviders: true,
        ShowOnlyNonStopFlight: false,
        IncludeSubLocations: true
    };

    try {
        const response = await makeApiRequest('/api/productservice/pricesearch', requestBody, logApiCall);
        const body = response.body;

        const seenOutbound = new Set<string>();
        const seenReturn = new Set<string>();

        // Add existing baseline to seen set so we don't duplicate
        outboundFlights.forEach(f => seenOutbound.add(`${f.airline}-${f.flightNumber}-${f.departureTime}`));
        returnFlights.forEach(f => seenReturn.add(`${f.airline}-${f.flightNumber}-${f.departureTime}`));

        const hotels = body?.hotels || [];

        // Use the lowest price found as basePrice if not set
        if (!basePrice && hotels.length > 0 && hotels[0].offers?.[0]?.price?.amount) {
            basePrice = hotels[0].offers[0].price.amount;
        }

        // Collect all offers to fetch details for
        // Limit to reasonable number to avoid spamming API
        const offersToFetch: any[] = [];
        for (const hotel of hotels) {
            if (hotel.offers) offersToFetch.push(...hotel.offers);
        }

        // Take max 30 offers to check for alternatives
        const limitedOffers = offersToFetch.slice(0, 30);
        console.log(`🔍 Checking ${limitedOffers.length} offers for alternative flights...`);

        const BATCH_SIZE = 5;
        for (let i = 0; i < limitedOffers.length; i += BATCH_SIZE) {
            const batch = limitedOffers.slice(i, i + BATCH_SIZE);

            await Promise.all(batch.map(async (offer: any) => {
                try {
                    // Critical: Get details to find real third-party flights
                    const details = await getOfferDetails(offer.offerId, () => { });

                    if (!details) return;

                    const processDirection = (seg: FlightSegment, direction: 'outbound' | 'return') => {
                        if (!seg) return;

                        const airline = resolveAirlineName(seg.airline, seg.airlineCode, seg.flightNumber);
                        const flightNo = String(seg.flightNumber || '');
                        const depTime = seg.departureTime || '';

                        // Phase 3: Skip FAKE flights in getFlightOffers
                        if (flightNo.toUpperCase().includes('FAKE') || String(offer.offerId || '').toUpperCase().includes('FAKE')) {
                            console.log(`🚫 Skipping FAKE flight in getFlightOffers: ${flightNo}`);
                            return;
                        }

                        // Dedupe
                        const flightKey = `${airline}-${flightNo}-${depTime}-${direction}`;
                        if (direction === 'outbound') {
                            if (seenOutbound.has(flightKey)) return;
                            seenOutbound.add(flightKey);
                        } else {
                            if (seenReturn.has(flightKey)) return;
                            seenReturn.add(flightKey);
                        }

                        // Phase 1: Price Diff Calculation — allow negative deltas
                        const offerPrice = (typeof (details as any).price === 'object' ? (details as any).price?.amount : (details as any).price) || (typeof (offer as any).price === 'object' ? (offer as any).price?.amount : (offer as any).price) || 0;
                        // Skip flights with undefined/zero price (can't compute meaningful delta)
                        if (!offerPrice) return;
                        const totalDiff = offerPrice - basePrice; // Allow negative
                        const diffPerLeg = parseFloat((totalDiff / 2).toFixed(2)); // Split diff

                        const opt: FlightOfferOption = {
                            offerId: offer.offerId,
                            flightGroupId: `${direction}-${flightNo}-${Math.random().toString(36).substr(2, 4)}`,
                            direction: direction,
                            airline: airline,
                            airlineCode: '',
                            flightNumber: flightNo,
                            departureAirport: seg.departureAirport,
                            arrivalAirport: seg.arrivalAirport,
                            departureTime: depTime,
                            arrivalTime: seg.arrivalTime || '',
                            flightDate: depTime.split('T')[0] || checkIn,
                            duration: seg.duration || '',
                            stops: seg.stops || 0,
                            logoUrl: seg.logoUrl || '',
                            priceDifference: diffPerLeg,
                            currency: (typeof (details as any).price === 'object' ? (details as any).price?.currency : (details as any).currency) || 'DKK',
                            isSelected: false
                        };

                        if (direction === 'outbound') outboundFlights.push(opt);
                        else returnFlights.push(opt);
                    };

                    if (details.outboundFlight) processDirection(details.outboundFlight, 'outbound');
                    if (details.returnFlight) processDirection(details.returnFlight, 'return');

                } catch (e) {
                    console.warn(`Failed to fetch details for offer ${offer.offerId}`, e);
                }
            }));
        }

    } catch (error) {
        console.warn('âŒ Alternative flight search failed (using baseline only):', error);
    }

    console.log('âœ… Final Flight offers:', outboundFlights.length, 'outbound,', returnFlights.length, 'return');

    return {
        outboundFlights,
        returnFlights,
        basePrice,
        currency: currency || 'DKK'
    };
};

export const getHotelPriceCalendar = async (query: SearchQuery, hotelId: string, logApiCall: (log: ApiLogEntry) => void): Promise<{ date: string, price: number }[]> => {
    const baseDate = new Date(query.checkIn);
    // Strict Product Type 1
    const productType = 1;
    const departureLocations = query.departureId ? [{ Id: query.departureId, Type: 2 }] : [];

    const datesToFetch: string[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = -7; i <= 7; i++) {
        const d = new Date(baseDate);
        d.setDate(baseDate.getDate() + i);
        if (d >= today) {
            datesToFetch.push(d.toISOString().split('T')[0]);
        }
    }

    if (datesToFetch.length === 0) return [];

    const fetchPriceForDate = async (date: string): Promise<{ date: string, price: number } | null> => {
        const requestBody = {
            ProductType: productType,
            DepartureLocations: departureLocations,
            ArrivalLocations: [{ Id: query.destinationId, Type: 2 }],
            CheckIn: `${date}T00:00:00`,
            Night: query.nights,
            Products: [{ Code: hotelId }],
            RoomCriteria: [{ Adult: query.adults, ChildAges: query.childrenAges || [] }],
            Nationality: 'SE',
            currency: query.currency,
            Culture: 'en-US',
        };
        try {
            const response = await makeApiRequest('/api/productservice/pricesearch', requestBody, () => { });
            const body = response.body;
            if (body.hotels && body.hotels.length > 0) {
                const offers = body.hotels[0].offers;
                if (offers && offers.length > 0) {
                    const minPrice = Math.min(...offers.map((o: any) => o.price?.amount || Infinity));
                    if (minPrice !== Infinity) { return { date, price: minPrice }; }
                }
            }
            return null;
        } catch (e) { return null; }
    };
    const results: { date: string, price: number }[] = [];
    const batchSize = 5;
    for (let i = 0; i < datesToFetch.length; i += batchSize) {
        const batch = datesToFetch.slice(i, i + batchSize);
        const batchResults = await Promise.all(batch.map(date => fetchPriceForDate(date)));
        batchResults.forEach(r => { if (r) results.push(r); });
    }
    return results.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
};

export const prepareBookingTransaction = async (bookingRequest: BookingRequest, logApiCall: (log: ApiLogEntry) => void, sessionId?: string): Promise<string> => {
    // If we have a sessionId from the booking flow, reuse the existing transactionId
    if (sessionId) {
        const txResponse = await fetch(`/api/booking-flow/transaction/${sessionId}`);
        const txData = await txResponse.json();

        if (logApiCall) {
            logApiCall({
                timestamp: new Date().toISOString(),
                endpoint: `/api/booking-flow/transaction/${sessionId}`,
                request: { sessionId },
                response: JSON.stringify(txData),
                status: txData.success ? 'success' : 'error'
            });
        }

        if (txData.success && txData.transactionId) {
            return txData.transactionId;
        }
        // Fall through to legacy /prepare if session lookup fails
        console.warn('Session lookup failed, falling back to /booking/prepare');
    }

    // Legacy fallback: Call /booking/prepare (creates new transaction)
    const body = {
        offerId: bookingRequest.offer.id,
        passengers: bookingRequest.passengers,
        contact: bookingRequest.contact,
        currency: bookingRequest.offer.currency,
        culture: 'en-US'
    };

    const response = await fetch('/api/tourvisio/booking/prepare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    });

    const data = await response.json();

    if (logApiCall) {
        logApiCall({
            timestamp: new Date().toISOString(),
            endpoint: '/api/tourvisio/booking/prepare',
            request: body,
            response: JSON.stringify(data),
            status: data.success ? 'success' : 'error'
        });
    }

    if (!data.success || !data.transactionId) {
        throw new Error(data.error || 'Failed to prepare booking');
    }

    return data.transactionId;
};

export const getPaymentOptions = async (transactionId: string, logApiCall: (log: ApiLogEntry) => void): Promise<string[]> => {
    const response = await fetch('/api/tourvisio/booking/payment-options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactionId })
    });
    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error || 'Failed to get payment options');
    }
    return data.options || [];
};

export const getPaymentOptionDetail = async (transactionId: string, optionId: string, logApiCall: (log: ApiLogEntry) => void): Promise<PaymentGroup[]> => {
    const response = await fetch('/api/tourvisio/booking/payment-details', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactionId, paymentOption: optionId })
    });
    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error || 'Failed to get payment details');
    }

    // Check if backend returns array or wrapped object
    const result = data.groups || [];
    return Array.isArray(result) ? result : [result];
};

export const beginPaymentTransaction = async (request: BeginPaymentTransactionRequest, logApiCall: (log: ApiLogEntry) => void): Promise<PaymentTransactionResponse> => {
    const response = await fetch('/api/tourvisio/booking/begin-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request)
    });
    const data = await response.json();
    // Wrap to match expected interface (response.body, response.header) if needed, 
    // or just return data if the component handles it transparently.
    // The backend returns the raw Tourvisio response from service.beginPaymentTransaction.
    return data;
};

export const commitBookingTransaction = async (transactionId: string, bookingRequest: BookingRequest, logApiCall: (log: ApiLogEntry) => void): Promise<ReservationResult> => {
    const response = await fetch('/api/tourvisio/booking/finalize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactionId })
    });
    const data = await response.json();

    if (!data.success) {
        throw new Error(data.error || 'Failed to finalize booking');
    }

    const resInfo = data.reservation?.body || data.reservation;

    return {
        reservationNumber: resInfo?.reservationNumber || 'PENDING',
        status: 'Confirmed',
        totalPrice: bookingRequest.offer.price,
        currency: bookingRequest.offer.currency,
        hotelName: bookingRequest.hotel.name,
        checkIn: bookingRequest.offer.checkIn,
        checkOut: ''
    };
};

// ==========================================
// CONTENT & RULES FUNCTIONS
// ==========================================

export const getContents = async (logApiCall: (log: ApiLogEntry) => void): Promise<any[]> => {
    const response = await fetch('/api/booking-flow/contents');
    const data = await response.json();
    return data.contents || [];
};

export const getContentDetail = async (code: string, logApiCall: (log: ApiLogEntry) => void, culture: string = 'en-US'): Promise<string> => {
    const response = await fetch(`/api/booking-flow/content/${code}?culture=${culture}`);
    const data = await response.json();
    // Backend returns { success: true, code: '...', content: '<html>...' }
    return data.content || '';
};

export const getFareRules = async (transactionId: string, logApiCall: (log: ApiLogEntry) => void): Promise<any> => {
    const response = await fetch('/api/tourvisio/flight/rules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transactionId })
    });
    const data = await response.json();
    return data.rules;
};

export const getTransactionStatus = async (transactionId: string, logApiCall: (log: ApiLogEntry) => void): Promise<GetTransactionStatusResponse> => {
    const body = { TransactionId: transactionId };
    return await makeApiRequest('/api/transactionservice/gettransactionstatus', body, logApiCall);
};

// ==========================================
// GOOGLE HOTELS / SERP API FUNCTIONS
// ==========================================

export interface GoogleRoom {
    name: string;
    images: string[];
    source: string;
    price: number | null;
    currency: string;
}

export interface GoogleHotelDetails {
    name: string;
    description: string;
    images: { thumbnail: string; original: string }[];
    rooms: GoogleRoom[]; // Room-level data with images from Booking.com/Hotels.com
    amenities: string[];
    rating: number | null;
    reviewCount: number;
    hotelClass: number | null;
    locationRating: number | null;
    checkInTime: string | null;
    checkOutTime: string | null;
    nearbyPlaces: { name: string; walkingTime?: string }[];
    ratingsBreakdown: { stars: number; count: number }[];
    reviewsBreakdown: { name: string; positive: number; negative: number }[];
    ecoCertified: boolean;
    propertyToken: string | null;
    gpsCoordinates: { latitude: number; longitude: number } | null;
}

/**
 * Fetch hotel details from Google Hotels via SERP API
 * Returns images, description, amenities from Google
 */
export const getGoogleHotelDetails = async (
    hotelName: string,
    location?: string,
    checkIn?: string,
    checkOut?: string
): Promise<GoogleHotelDetails | null> => {
    try {
        const params = new URLSearchParams({ hotelName });
        if (location) params.append('location', location);
        if (checkIn) params.append('checkIn', checkIn);
        if (checkOut) params.append('checkOut', checkOut);

        const response = await fetch(`/api/tourvisio/google-hotel-details?${params}`);

        if (!response.ok) {
            console.error(`Google Hotel Details API error: ${response.status}`);
            return null;
        }

        const result = await response.json();

        if (result.success && result.data) {
            return result.data as GoogleHotelDetails;
        }

        return null;
    } catch (error) {
        console.error('Failed to fetch Google Hotel details:', error);
        return null;
    }
};

// ==========================================
// SANFLIGHT REAL FLIGHT SEARCH (ProductType 3)
// ==========================================

export interface BrandedFareFeature {
    serviceGroup: number;   // FlightBrandFeatureServiceGroup enum
    label: string;          // e.g. "1 Piece X 8 Kg Cabin Baggage"
    pricingType: number;    // FlightBrandPricingType: 0=FREE, 1=CHARGEABLE, 2=NOT_OFFERED
}

export interface BrandedFare {
    name: string;           // e.g. "Ecofly", "Extrafly", "Flexfly", "Primefly"
    priceDifference: number;
    currency: string;
    features: BrandedFareFeature[];
    offerId?: string;       // Offer ID for this specific fare tier
    isSelected?: boolean;
}

export interface RealFlightOption {
    id: string;
    offerId?: string;
    airline: string;
    airlineCode: string;
    flightNumber: string;
    departureAirport: string;
    arrivalAirport: string;
    departureTime: string;
    arrivalTime: string;
    departureDate: string;
    duration: string;
    stops: number;
    price: number;
    priceDifference: number;
    currency: string;
    logoUrl: string;
    segments?: any[];
    baggage?: string;
    brandedFares?: BrandedFare[];
}

/**
 * Search for real flights using SanFlight ProductType 3
 * This returns actual flight schedules instead of FAKE placeholder flights
 */
export const searchRealFlights = async (
    departureAirport: string,
    arrivalAirport: string,
    departureDate: string,
    returnDate: string,
    adults: number = 2,
    children: number = 0,
    logApiCall: (log: ApiLogEntry) => void,
    departureId?: string,
    arrivalId?: string,
    hotelId?: string,
    culture?: string,
    currency?: string
): Promise<{ outbound: RealFlightOption[]; inbound: RealFlightOption[] }> => {
    const settings = getApiSettings();
    const serviceAddress = settings.serviceAddress;

    const nights = Math.ceil(
        (new Date(returnDate).getTime() - new Date(departureDate).getTime()) / (1000 * 60 * 60 * 24)
    );

    const requestBody = {
        ProductType: 1, // Holiday Package - use charter flights (ProductType 3 has SanFlightV2 issues)
        ServiceTypes: [1, 3], // 1=Charter, 3=Scheduled flights
        DepartureLocations: [
            departureId ? { Id: departureId, Type: 2 } : { Name: departureAirport.toUpperCase(), Type: 2 }
        ],
        ArrivalLocations: [
            arrivalId ? { Id: arrivalId, Type: 2 } : { Name: arrivalAirport.toUpperCase(), Type: 2 }
        ],
        CheckIn: `${departureDate}T00:00:00`,
        Night: nights,
        RoomCriteria: [{ Adult: adults, ChildAges: Array(children).fill(8) }],
        Nationality: 'SE',
        Culture: 'en-US', // USER RULE: "dil olarak sorgu hep en us olmalı"
        Currency: currency || 'DKK',
        GetTransportations: true,
        forceFlightBundlePackage: false,
        ShowOnlyNonStopFlight: false,
        IncludeSubLocations: true,
        Products: [] // FATAL BUGFIX: Tourvisio crashes if Products contains a Hotel ID when ProductType is 1!
    };

    console.log('✈️  Searching flights via Holiday Package (ProductType 1):', departureAirport, '→', arrivalAirport);
    console.log('📦 Search Payload:', JSON.stringify(requestBody));

    try {
        const response = await makeApiRequest('/api/productservice/pricesearch', requestBody, logApiCall);
        const body = response.body;

        const outboundFlights: RealFlightOption[] = [];
        const inboundFlights: RealFlightOption[] = [];

        // Process flight offers from Holiday Package (ProductType 1) response
        // Hotels contain offers with transportations
        let hotels = body?.hotels || [];
        
        // BUGFIX FILTERING: Since we couldn't send the hotelId to the API, filter it locally Now!
        // Note: Tourvisio returns 'id', not 'hotelId' inside the pricesearch response!
        console.log(`[DEBUG] pricesearch returned ${hotels.length} hotels in total.`);
        if (hotels.length > 0) {
            console.log(`[DEBUG] IDs received: `, hotels.map((h:any) => h.id).join(', '));
        }

        if (hotelId) {
            hotels = hotels.filter((h: any) => String(h.id) === String(hotelId));
            console.log(`🏨 Found ${hotels.length} specific hotel matches for ${hotelId} inside package results.`);
        }

        const allOffers: any[] = [];
        for (const hotel of hotels) {
            const hotelOffers = hotel.offers || [];
            allOffers.push(...hotelOffers);
        }

        // Fallback to direct offers if available
        if (allOffers.length === 0 && body?.offers) {
            allOffers.push(...body.offers);
        }

        // Calculate base price for price difference
        const minPrice = allOffers.length > 0
            ? Math.min(...allOffers.map((o: any) => o.price?.amount || Infinity))
            : 0;

        allOffers.forEach((offer: any) => {
            const transportations = offer.transportations || [];
            const numLegs = transportations.length || 1;
            const offerPrice = offer.price?.amount || 0;
            const totalDiff = offerPrice - minPrice;
            const diffPerLeg = totalDiff / numLegs;

            transportations.forEach((trans: any) => {
                const isOutbound = trans.direction === 0;
                const segments = trans.segments || [trans];
                const firstSeg = segments[0] || trans;
                const lastSeg = segments[segments.length - 1] || trans;

                const dep = firstSeg.departure || {};
                const arr = lastSeg.arrival || {};
                const airline = firstSeg.airline || {};

                // Extract real times
                const depTime = dep.date || dep.time || '';
                const arrTime = arr.date || arr.time || '';

                // Format times properly
                let formattedDepTime = '';
                let formattedArrTime = '';
                let flightDateStr = '';

                if (depTime && typeof depTime === 'string' && depTime.includes('T')) {
                    const d = new Date(depTime);
                    formattedDepTime = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
                    flightDateStr = d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
                }

                if (arrTime && typeof arrTime === 'string' && arrTime.includes('T')) {
                    const d = new Date(arrTime);
                    formattedArrTime = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
                }

                // Duration
                const duration = trans.duration || 0;
                const hours = Math.floor(duration / 60);
                const mins = duration % 60;
                const durationStr = duration > 0 ? `${hours}h ${mins}m` : '';

                // Airline logo
                let logoUrl = airline.logo || airline.thumbnail || '';
                if (logoUrl && !logoUrl.startsWith('http')) {
                    logoUrl = `${serviceAddress.replace(/\/v2$/i, '')}/media/${logoUrl.replace(/^[~/]+/, '')}`;
                }
                if (!logoUrl && airline.code) {
                    logoUrl = `https://images.kiwi.com/airlines/64/${airline.code}.png`;
                }

                const flightOption: RealFlightOption = {
                    id: `${offer.offerId}-${trans.direction}`,
                    airline: resolveAirlineName(airline.name, airline.code, firstSeg.flightNo || firstSeg.flightNumber),
                    airlineCode: airline.code || '',
                    flightNumber: firstSeg.flightNo || firstSeg.flightNumber || '',
                    departureAirport: dep.airport?.code || dep.airport?.id || departureAirport,
                    arrivalAirport: arr.airport?.code || arr.airport?.id || arrivalAirport,
                    departureTime: formattedDepTime || '09:00',
                    arrivalTime: formattedArrTime || '13:30',
                    departureDate: flightDateStr,
                    duration: durationStr,
                    stops: segments.length - 1,
                    price: offerPrice,
                    priceDifference: diffPerLeg,
                    currency: offer.price?.currency || 'DKK',
                    logoUrl: logoUrl,
                    segments: segments
                };

                if (isOutbound) {
                    outboundFlights.push(flightOption);
                } else {
                    inboundFlights.push(flightOption);
                }
            });
        });

        console.log(`âœ… Found ${outboundFlights.length} outbound, ${inboundFlights.length} inbound charter flights`);

        return { outbound: outboundFlights, inbound: inboundFlights };

    } catch (error) {
        console.error('âŒ Charter flight search failed:', error);
        return { outbound: [], inbound: [] };
    }
};

// ==========================================
// BACKEND getPagingData INTEGRATION
// Uses backend /api/tourvisio endpoints instead of direct API calls
// ==========================================

export interface AlternativeFlightOption {
    id: string;
    offerId: string;
    direction: 'outbound' | 'inbound';
    flightNumber: string;
    airline: string;
    airlineLogo: string;
    departureAirport: string;
    departureCode: string;
    departureTime: string;
    arrivalAirport: string;
    arrivalCode: string;
    arrivalTime: string;
    flightDate: string;
    duration: string;
    stops: number;
    priceDifference: number;
    currency: string;
    isDefault: boolean;
    isAvailable: boolean;
}

export interface AlternativeFlightsResult {
    outbound: AlternativeFlightOption[];
    inbound: AlternativeFlightOption[];
    totalCount: number;
    hasMore: boolean;
    fromCache: boolean;
}

/**
 * Fetch alternative flights using backend getPagingData endpoint
 * This uses the local backend which calls Tourvisio's getpagingdata API
 * @param searchId - The searchId from a previous priceSearch
 * @param filters - Optional filters for flights
 * @param page - Page number for pagination
 * @param limit - Number of results per page
 */
export const getAlternativeFlightsFromBackend = async (
    searchId: string,
    filters: { airlines?: string[]; maxStops?: number; departureTimeRange?: string } = {},
    page: number = 1,
    limit: number = 20
): Promise<AlternativeFlightsResult> => {
    try {
        const response = await fetch('/api/tourvisio/alternative-flights', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ searchId, filters, page, limit })
        });

        if (!response.ok) {
            throw new Error(`Backend alternative-flights failed: ${response.status}`);
        }

        const data = await response.json();

        if (!data.success) {
            throw new Error(data.error || 'Failed to fetch alternative flights');
        }

        // Transform backend response to frontend format
        const outbound: AlternativeFlightOption[] = [];
        const inbound: AlternativeFlightOption[] = [];

        (data.flights || []).forEach((flight: any) => {
            const option: AlternativeFlightOption = {
                id: flight.id || flight._id,
                offerId: flight.offerId,
                direction: flight.direction || (flight.outbound ? 'outbound' : 'inbound'),
                flightNumber: flight.flightNumber || flight.outbound?.flightNumber || flight.inbound?.flightNumber || '',
                airline: resolveAirlineName(flight.airline || flight.outbound?.airline?.name || flight.inbound?.airline?.name, flight.airlineCode || flight.departureCode, flight.flightNumber || flight.outbound?.flightNumber || flight.inbound?.flightNumber),
                airlineLogo: flight.airlineLogo || flight.outbound?.airline?.logo || flight.inbound?.airline?.logo || '',
                departureAirport: flight.departureAirport || flight.outbound?.departure?.airport || flight.inbound?.departure?.airport || '',
                departureCode: flight.departureCode || flight.outbound?.departure?.code || flight.inbound?.departure?.code || '',
                departureTime: flight.departureTime || flight.outbound?.departure?.time || flight.inbound?.departure?.time || '',
                arrivalAirport: flight.arrivalAirport || flight.outbound?.arrival?.airport || flight.inbound?.arrival?.airport || '',
                arrivalCode: flight.arrivalCode || flight.outbound?.arrival?.code || flight.inbound?.arrival?.code || '',
                arrivalTime: flight.arrivalTime || flight.outbound?.arrival?.time || flight.inbound?.arrival?.time || '',
                flightDate: flight.flightDate || '',
                duration: flight.duration || flight.outbound?.duration || flight.inbound?.duration || '',
                stops: flight.stops ?? flight.outbound?.stops ?? flight.inbound?.stops ?? 0,
                priceDifference: flight.priceDifference || 0,
                currency: flight.currency || 'DKK',
                isDefault: flight.isDefault || false,
                isAvailable: flight.isAvailable !== false
            };

            if (option.direction === 'outbound') {
                outbound.push(option);
            } else {
                inbound.push(option);
            }
        });

        return {
            outbound,
            inbound,
            totalCount: data.totalCount || outbound.length + inbound.length,
            hasMore: data.hasMore || false,
            fromCache: data.fromCache || false
        };
    } catch (error) {
        console.error('âŒ getAlternativeFlightsFromBackend error:', error);
        return {
            outbound: [],
            inbound: [],
            totalCount: 0,
            hasMore: false,
            fromCache: false
        };
    }
};

/**
 * Fetch flight options for a specific offer using backend flight-options endpoint
 * This first checks the cache, then fetches from API via getOfferDetails
 * @param offerId - The offerId to get flight options for
 * @param searchId - Optional searchId for additional filtering
 * @param direction - 'outbound', 'inbound', or 'all'
 */
export const getFlightOptionsFromBackend = async (
    offerId: string,
    searchId?: string,
    direction: 'outbound' | 'inbound' | 'all' = 'all'
): Promise<{ flightOptions: AlternativeFlightOption[]; fromCache: boolean }> => {
    try {
        const params = new URLSearchParams({ direction });
        if (searchId) params.append('searchId', searchId);

        const response = await fetch(`/api/tourvisio/flight-options/${offerId}?${params}`);

        if (!response.ok) {
            throw new Error(`Backend flight-options failed: ${response.status}`);
        }

        const data = await response.json();

        if (!data.success) {
            throw new Error(data.error || 'Failed to fetch flight options');
        }

        const flightOptions: AlternativeFlightOption[] = (data.flightOptions || []).map((f: any) => ({
            id: f.id || f._id,
            offerId: f.offerId,
            direction: f.direction || 'outbound',
            flightNumber: f.flightNumber || f.outbound?.flightNumber || '',
            airline: resolveAirlineName(f.airline || f.outbound?.airline, f.airlineCode || f.departureCode, f.flightNumber || f.outbound?.flightNumber),
            airlineLogo: f.airlineLogo || f.outbound?.airlineLogo || '',
            departureAirport: f.departureAirport || f.outbound?.departureAirport || '',
            departureCode: f.departureCode || f.outbound?.departureCode || '',
            departureTime: f.departureTime || f.outbound?.departureTime || '',
            arrivalAirport: f.arrivalAirport || f.outbound?.arrivalAirport || '',
            arrivalCode: f.arrivalCode || f.outbound?.arrivalCode || '',
            arrivalTime: f.arrivalTime || f.outbound?.arrivalTime || '',
            flightDate: f.flightDate || '',
            duration: f.duration || f.outbound?.duration || '',
            stops: f.stops ?? f.outbound?.stops ?? 0,
            priceDifference: f.priceDifference || 0,
            currency: f.currency || 'DKK',
            isDefault: f.isDefault || false,
            isAvailable: f.isAvailable !== false
        }));

        return {
            flightOptions,
            fromCache: data.fromCache || false
        };
    } catch (error) {
        console.error('âŒ getFlightOptionsFromBackend error:', error);
        return {
            flightOptions: [],
            fromCache: false
        };
    }
};

/**
 * Generic getPagingData call to backend
 * This can be used for any paging data needs (hotels, flights, etc.)
 */
export const getPagingDataFromBackend = async (
    searchId: string,
    pagingOptions: { productType: number; pageSize?: number; pageIndex?: number; hotelCode?: string }[],
    currency: string = 'SEK',
    culture: string = 'en-US'
): Promise<{ hotels?: any[]; products?: any[]; success: boolean; error?: string }> => {
    try {
        const response = await fetch('/api/tourvisio/get-paging-data', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ searchId, pagingOptions, currency, culture })
        });

        if (!response.ok) {
            throw new Error(`Backend get-paging-data failed: ${response.status}`);
        }

        const data = await response.json();

        return {
            hotels: data.hotels,
            products: data.products,
            success: data.success,
            error: data.error
        };
    } catch (error) {
        console.error('âŒ getPagingDataFromBackend error:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error'
        };
    }
};

/**
 * Supplementary/Other Flights iÃ§in GetPagingData Ã§aÄŸrÄ±sÄ±
 * "DiÄŸer UÃ§uÅŸlar" butonu iÃ§in kullanÄ±lÄ±r
 * Load-more pattern: Her Ã§aÄŸrÄ±da currentPage artar
 * 
 * @param searchId - priceSearch'ten gelen searchId
 * @param currentPage - Sayfa numarasÄ± (1'den baÅŸlar)
 * @param pageRowCount - Sayfa baÅŸÄ±na sonuÃ§ (varsayÄ±lan: 10)
 */
export interface SupplementaryFlight {
    id: string;
    airline: string;
    airlineCode: string;
    flightNumber: string;
    departureAirport: string;
    arrivalAirport: string;
    departureTime: string;
    arrivalTime: string;
    duration: string;
    stops: number;
    logoUrl: string;
    price: number | null;
    currency: string;
    direction: 'outbound' | 'return';
    offerId?: string;
    baggage?: string;
    segments?: any[];
    /** 'package' = price is total package (compare vs basePrice), 'flight' = flight-only (compare vs cheapest flight) */
    priceType?: 'package' | 'flight';
}

export interface SupplementaryFlightsResult {
    success: boolean;
    flights: SupplementaryFlight[];
    totalCount: number;
    currentPage: number;
    hasMore: boolean;
    nextPage: number | null;
    error?: string;
}


export const getSupplementaryFlights = async (
    searchId: string,
    currentPage: number = 1,
    pageRowCount: number = 9999, // User requested "infinite" / all options
    currency: string = 'SEK',
    culture: string = 'en-US',
    signal?: AbortSignal
): Promise<SupplementaryFlightsResult> => {
    try {
        console.log(`âœˆï¸ getSupplementaryFlights: searchId=${searchId}, page=${currentPage}, culture=${culture}`);

        if (!searchId || searchId === 'NO_SEARCH_ID_PROVIDED') {
            console.warn('âš ï¸ Invalid searchId for getSupplementaryFlights');
            return {
                success: false,
                flights: [],
                totalCount: 0,
                currentPage,
                hasMore: false,
                nextPage: null,
                error: 'Invalid searchId'
            };
        }

        const requestBody = {
            currency: currency,
            culture: culture,
            pagingOptions: [
                {
                    currentPage: currentPage,
                    pageRowCount: pageRowCount,
                    productType: 2,
                    getFilters: true,
                    filters: [],
                    getPagingCount: true,
                    isNewPagingRequest: currentPage === 1
                },
                // AŞAMA 4: productType: 3 (Uçuş) — alternatif uçuş kombinasyonlarını çeker
                {
                    currentPage: 1,
                    pageRowCount: 50,
                    productType: 3,
                    getFilters: false,
                    filters: [],
                    getPagingCount: true,
                    isNewPagingRequest: currentPage === 1
                }
            ],
            searchId: searchId
        };

        // 1. Get Paging Data
        const response = await makeApiRequest('/api/productservice/getpagingdata', requestBody, (log) => console.log('Paging API Call:', log), 0, signal);

        // 2. Process Response — extract offers from productType:2 AND flights from productType:3
        const offers = response?.body?.offers || response?.body?.hotels?.[0]?.offers || [];
        const flightProducts = response?.body?.flights || response?.body?.products || [];

        console.log(`📋 Paging response: ${offers.length} offers (pType 2), ${flightProducts.length} flight products (pType 3)`);

        if (offers.length === 0 && flightProducts.length === 0) {
            console.warn('Supplementary flights request returned no offers or flights:', response?.header?.messages);
            return {
                success: false,
                flights: [],
                totalCount: 0,
                currentPage,
                hasMore: false,
                nextPage: null,
                error: response?.header?.messages?.[0]?.message || 'No offers found'
            };
        }

        const mappedFlights: SupplementaryFlight[] = [];
        const serviceAddress = getApiSettings().serviceAddress;

        console.log(`Found ${offers.length} offers. Fetching details for each to get real flight info...`);

        // Helper to resolve unitType to string
        const unitTypeToStr = (unitType: any): string => {
            if (unitType === 0 || unitType === '0') return 'kg';
            if (unitType === 2 || unitType === '2') return 'Piece';
            return 'kg'; // default
        };

        // Helper to map FlightSegment to SupplementaryFlight
        const mapToSupFlight = (seg: any, price: any, direction: 'outbound' | 'return', offerId: string, priceType: 'package' | 'flight' = 'package'): SupplementaryFlight => {
            const dur = seg.duration || 0;

            // Handle price: getOfferDetails returns result.price as a plain number,
            // but productType:3 passes {amount, currency} objects
            let resolvedPrice: number | null = null;
            if (typeof price === 'number') {
                resolvedPrice = price;
            } else if (typeof price === 'object' && price !== null) {
                resolvedPrice = price.amount ?? price.value ?? null;
            }

            // Resolve baggage — also check segments[0].baggageInformations
            let baggage = seg.baggage || seg.cabinClass || '';
            if (!baggage && seg.segments?.[0]?.baggageInformations?.[0]) {
                const bi = seg.segments[0].baggageInformations[0];
                const unit = bi.unit || unitTypeToStr(bi.unitType);
                baggage = bi.weight ? `${bi.weight} ${unit}` : (bi.description || '');
            }

            // Resolve stops from segments
            const segStops = seg.stops || seg.stopCount || (seg.segments ? Math.max(0, seg.segments.length - 1) : 0);

            console.log(`💰 mapToSupFlight: offerId=${offerId}, direction=${direction}, priceType=${priceType}, resolvedPrice=${resolvedPrice}, stops=${segStops}, baggage=${baggage}`);

            const depTime = String(seg.departureTime || seg.segments?.[0]?.departureTime || seg.segments?.[0]?.departure?.date || seg.segments?.[0]?.departure?.dateTime || '');
            const arrTime = String(seg.arrivalTime || seg.segments?.[0]?.arrivalTime || seg.segments?.[0]?.arrival?.date || seg.segments?.[0]?.arrival?.dateTime || '');

            const formatTime = (isoStr: string) => {
                if (!isoStr) return '';
                try {
                    const d = new Date(isoStr);
                    if (isNaN(d.getTime())) {
                        const match = isoStr.match(/(\d{2}:\d{2})/);
                        return match ? match[1] : isoStr;
                    }
                    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
                } catch { return isoStr; }
            };

            return {
                id: `${offerId}-${direction}-${Math.random().toString(36).substr(2, 5)}`,
                airline: resolveAirlineName(typeof seg.airline === 'object' ? seg.airline?.name : seg.airline, seg.airlineCode, seg.flightNumber),
                airlineCode: seg.airlineCode || '',
                flightNumber: String(seg.flightNumber || ''),
                departureAirport: String(seg.departureAirport || ''),
                arrivalAirport: String(seg.arrivalAirport || ''),
                departureTime: formatTime(depTime),
                arrivalTime: formatTime(arrTime),
                duration: dur ? `${Math.floor(dur / 60)}h ${dur % 60}m` : '',
                stops: segStops,
                logoUrl: seg.logoUrl || '',
                price: resolvedPrice,
                currency: (typeof price === 'object' ? price?.currency : null) || currency,
                direction: direction,
                offerId: String(offerId || ''),
                baggage: String(baggage),
                segments: seg.segments || [],
                priceType: priceType
            };
        };

        // BATCH PROCESSING — Optimization: limit to first 10 offers to reduce network traffic ~80%
        const MAX_OFFER_DETAILS = 10;
        const limitedOffers = offers.slice(0, MAX_OFFER_DETAILS);
        if (offers.length > MAX_OFFER_DETAILS) {
            console.log(`📊 Limiting getOfferDetails to ${limitedOffers.length}/${offers.length} offers (saving ${offers.length - MAX_OFFER_DETAILS} API calls)`);
        }

        const BATCH_SIZE = 10;
        for (let i = 0; i < limitedOffers.length; i += BATCH_SIZE) {
            // Check if aborted before each batch
            if (signal?.aborted) {
                console.log('🛑 getSupplementaryFlights: Aborted before batch', Math.floor(i / BATCH_SIZE) + 1);
                break;
            }
            const batch = limitedOffers.slice(i, i + BATCH_SIZE);

            const results = await Promise.all(batch.map(async (offer: any) => {
                try {
                    if (signal?.aborted) return null;
                    // Critical: The user stated that calling getOfferDetails with the offerId from getPagingData returns the correct third-party flights.
                    // So we must perform this detailed fetch.
                    const details = await getOfferDetails(offer.offerId, () => { }, signal);
                    if (!details) return null;
                    return { offer, details };
                } catch (e: any) {
                    if (e?.name === 'AbortError') return null; // Silently ignore aborted requests
                    console.error(`Failed detailed fetch for offer ${offer.offerId}`, e);
                    return null;
                }
            }));

            for (const res of results) {
                if (!res) continue;
                const { offer, details } = res;

                // details.price is a plain number (resolved by getOfferDetails line 1207)
                // details.currency is the currency string
                const offerPrice = details.price || null;

                if (details.outboundFlight) {
                    mappedFlights.push(mapToSupFlight(details.outboundFlight, offerPrice, 'outbound', offer.offerId));
                }
                if (details.returnFlight) {
                    mappedFlights.push(mapToSupFlight(details.returnFlight, offerPrice, 'return', offer.offerId));
                }
            }
        }

        // FIX Bug 2: Extract productType:3 flight data DIRECTLY from paging response
        // Flight-only IDs (e.g. VF3103-VF265) CANNOT be queried via getofferdetails — that
        // endpoint only works for package offer IDs. Sending them causes "General Exception".
        if (flightProducts.length > 0) {
            console.log(`✈️ Processing ${flightProducts.length} productType:3 flight alternatives (direct extraction, no getOfferDetails)`);
            for (const fp of flightProducts) {
                try {
                    const fpId = fp.offerId || fp.id || `flight-${Math.random().toString(36).substr(2, 5)}`;

                    // Price resolution: Try multiple paths — user confirmed correct path is offers[0].price.amount
                    const resolveFpPrice = (): { amount: number; currency: string } => {
                        // Path 1: fp.offers[0].price.amount (most reliable per user)
                        const offerPrice = fp.offers?.[0]?.price;
                        if (offerPrice?.amount && offerPrice.amount > 0) {
                            console.log(`💰 PT3 price resolved via fp.offers[0].price.amount: ${offerPrice.amount}`);
                            return { amount: parseFloat(String(offerPrice.amount)), currency: offerPrice.currency || currency };
                        }
                        // Path 2: fp.price.amount
                        if (fp.price?.amount && fp.price.amount > 0) {
                            console.log(`💰 PT3 price resolved via fp.price.amount: ${fp.price.amount}`);
                            return { amount: parseFloat(String(fp.price.amount)), currency: fp.price.currency || currency };
                        }
                        // Path 3: fp.totalPrice
                        if (fp.totalPrice?.amount && fp.totalPrice.amount > 0) {
                            console.log(`💰 PT3 price resolved via fp.totalPrice.amount: ${fp.totalPrice.amount}`);
                            return { amount: parseFloat(String(fp.totalPrice.amount)), currency: fp.totalPrice.currency || currency };
                        }
                        // Path 4: fp.price as plain number
                        if (typeof fp.price === 'number' && fp.price > 0) {
                            console.log(`💰 PT3 price resolved via fp.price (number): ${fp.price}`);
                            return { amount: fp.price, currency: currency };
                        }
                        console.warn(`⚠️ PT3 price NOT FOUND for ${fpId}. fp.price=`, fp.price, ', fp.offers=', fp.offers);
                        return { amount: 0, currency: currency };
                    };

                    const resolvedFpPrice = resolveFpPrice();

                    // Extract flight segments from the paging response structure
                    const segments = fp.flights || fp.flightSegments || fp.items || [];
                    if (segments.length > 0) {
                        for (const seg of segments) {
                            const items = seg.items || [seg];
                            for (const item of items) {
                                let direction: 'outbound' | 'return' = 'outbound';
                                const itemRoute = item.route || seg.route;
                                if (itemRoute === 2) direction = 'return';

                                // Item-level price override: check item.offers[0].price.amount
                                let itemPrice = resolvedFpPrice.amount;
                                let itemCurrency = resolvedFpPrice.currency;
                                const itemOfferPrice = item.offers?.[0]?.price;
                                if (itemOfferPrice?.amount && itemOfferPrice.amount > 0) {
                                    itemPrice = parseFloat(String(itemOfferPrice.amount));
                                    itemCurrency = itemOfferPrice.currency || itemCurrency;
                                    console.log(`💰 PT3 item-level price override: ${itemPrice} (from item.offers[0].price.amount)`);
                                }

                                mappedFlights.push({
                                    id: `${fpId}-${direction}-${Math.random().toString(36).substr(2, 5)}`,
                                    airline: resolveAirlineName(item.airline?.name || item.airlineName || seg.airline?.name, item.airline?.code || item.airlineCode, item.flightNumber || item.flightNo),
                                    airlineCode: String(item.airline?.code || item.airlineCode || ''),
                                    flightNumber: String(item.flightNumber || item.flightNo || ''),
                                    departureAirport: String(item.departureAirport || item.departure?.name || item.departure?.airport?.name || ''),
                                    arrivalAirport: String(item.arrivalAirport || item.arrival?.name || item.arrival?.airport?.name || ''),
                                    departureTime: String(item.departureTime || item.departureDate || item.departure?.date || ''),
                                    arrivalTime: String(item.arrivalTime || item.arrivalDate || item.arrival?.date || ''),
                                    duration: item.duration ? `${Math.floor(item.duration / 60)}h ${item.duration % 60}m` : '',
                                    stops: item.stopCount || item.stops || (item.segments ? Math.max(0, item.segments.length - 1) : 0),
                                    logoUrl: item.airline?.logoFull || item.airline?.logo || item.logoUrl || '',
                                    price: itemPrice,
                                    currency: itemCurrency,
                                    direction,
                                    offerId: String(fpId),
                                    baggage: (() => {
                                        // Dynamic baggage from baggageInformations
                                        const bi = item.baggageInformations?.[0] || item.segments?.[0]?.baggageInformations?.[0];
                                        if (bi?.weight) {
                                            const unit = bi.unit || unitTypeToStr(bi.unitType);
                                            return `${bi.weight} ${unit}`;
                                        }
                                        // Fallback: flightBrandInfo.name
                                        const brandInfo = item.flightBrandInfo || item.segments?.[0]?.flightBrandInfo;
                                        if (brandInfo?.name) return brandInfo.name;
                                        return String(item.baggage || item.baggageAllowance || '');
                                    })(),
                                    segments: item.segments || [],
                                    priceType: 'flight'
                                });
                            }
                        }
                    } else {
                        // Flat flight product — no nested segments
                        mappedFlights.push({
                            id: `${fpId}-outbound-${Math.random().toString(36).substr(2, 5)}`,
                            airline: resolveAirlineName(fp.airline?.name || fp.airlineName, fp.airline?.code, fp.flightNumber || fp.flightNo),
                            airlineCode: fp.airline?.code || '',
                            flightNumber: fp.flightNumber || fp.flightNo || '',
                            departureAirport: fp.departureAirport || fp.departure?.name || '',
                            arrivalAirport: fp.arrivalAirport || fp.arrival?.name || '',
                            departureTime: fp.departureTime || fp.departureDate || '',
                            arrivalTime: fp.arrivalTime || fp.arrivalDate || '',
                            duration: fp.duration ? `${Math.floor(fp.duration / 60)}h ${fp.duration % 60}m` : '',
                            stops: fp.stopCount || fp.stops || 0,
                            logoUrl: fp.airline?.logoFull || fp.airline?.logo || '',
                            price: resolvedFpPrice.amount,
                            currency: resolvedFpPrice.currency,
                            direction: 'outbound',
                            offerId: fpId,
                            baggage: String(fp.baggage || ''),
                            segments: [],
                            priceType: 'flight'
                        });
                    }
                } catch (e) {
                    console.error('Failed mapping flight product:', e);
                }
            }
            console.log(`✅ Total flights after productType:3 direct extraction: ${mappedFlights.length}`);
        }

        // Phase 3: Filter out FAKE flights and flights with null/undefined price
        const cleanFlights = mappedFlights.filter(f => {
            // Filter FAKE IDs/flight numbers — use String() to prevent TypeError on non-string values
            const flightNum = String(f.flightNumber || '');
            const offId = String(f.offerId || '');
            const airlineStr = String(f.airline || '');
            const idStr = String(f.id || '');

            if (flightNum.toUpperCase().includes('FAKE')) {
                console.log(`🚫 Filtered FAKE flight: ${flightNum} (offerId: ${offId})`);
                return false;
            }
            if (f.departureTime === '00:00' || f.departureTime === '23:59') {
                console.log(`🚫 Filtered placeholder time flight: ${flightNum} at ${f.departureTime}`);
                return false;
            }
            if (offId.toUpperCase().includes('FAKE')) {
                console.log(`🚫 Filtered FAKE offerId: ${offId}`);
                return false;
            }
            if (idStr.toUpperCase().includes('FAKE')) {
                console.log(`🚫 Filtered FAKE id: ${idStr}`);
                return false;
            }
            // Filter flights with no valid price (can't compute delta)
            if (f.price === null || f.price === undefined) {
                console.log(`🚫 Filtered flight with null price: ${flightNum} (offerId: ${offId})`);
                return false;
            }
            // Filter flights with empty essential data (no departure or arrival)
            if (!f.departureAirport && !f.arrivalAirport && !flightNum) {
                console.log(`🚫 Filtered empty flight (no airports/number)`);
                return false;
            }
            return true;
        });

        console.log(`✅ After FAKE/null-price filter: ${cleanFlights.length} flights (removed ${mappedFlights.length - cleanFlights.length})`);

        return {
            success: true,
            flights: cleanFlights,
            totalCount: cleanFlights.length,
            currentPage,
            hasMore: cleanFlights.length >= pageRowCount,
            nextPage: cleanFlights.length >= pageRowCount ? currentPage + 1 : null
        };



    } catch (error: any) {
        const isAbort = error?.name === 'AbortError' || (error?.message && String(error.message).toLowerCase().includes('abort'));
        if (isAbort) {
            console.log('🛑 getSupplementaryFlights: Aborted by caller');
            return { success: false, flights: [], totalCount: 0, currentPage, hasMore: false, nextPage: null, error: 'Aborted' };
        }
        console.error('fetchNextPageWithFlights error:', error);
        return { success: false, flights: [], totalCount: 0, currentPage: 1, hasMore: false, nextPage: null, error: 'Error fetching flights' };
    }
};

// ==========================================
// RESERVATION MANAGEMENT (CANCEL/DOCS)
// ==========================================

export const getReservationDetail = async (reservationNumber: string, logApiCall: (log: ApiLogEntry) => void): Promise<any> => {
    const response = await fetch(`/api/tourvisio/reservation/${reservationNumber}`);
    const data = await response.json();
    
    // Backend returns { success, booking (from DB), tourvisioData (from Tourvisio API) }
    // Tourvisio API response is in tourvisioData?.body
    const tourvisioReservation = data.tourvisioData?.body;
    const dbBooking = data.booking;
    
    // Prefer Tourvisio data (has rich details), fallback to DB booking
    if (tourvisioReservation) {
        return tourvisioReservation;
    }
    
    // If no Tourvisio data but we have a DB booking, map it to a compatible format
    if (dbBooking) {
        return {
            reservationNumber: dbBooking.reservationNumber,
            status: { name: dbBooking.status || 'Pending' },
            createDate: dbBooking.createdAt,
            beginDate: dbBooking.offer?.checkIn || dbBooking.createdAt,
            endDate: dbBooking.offer?.checkOut || dbBooking.createdAt,
            totalPrice: { 
                amount: dbBooking.payment?.totalPrice || 0, 
                currency: dbBooking.payment?.currency || 'SEK' 
            },
            travellers: (dbBooking.passengers || []).map((p: any, idx: number) => ({
                name: p.firstName || '',
                surname: p.lastName || '',
                type: p.type === 'Adult' ? 1 : 2,
                birthDate: p.birthDate || '',
                gender: p.gender === 'Male' ? 1 : 2,
                nationality: p.nationality || ''
            })),
            services: [
                // Hotel service (type 1)
                ...(dbBooking.hotel ? [{
                    type: 1,
                    product: { name: dbBooking.hotel.name || 'Unknown Hotel' },
                    description: dbBooking.offer?.roomType || '',
                    board: { name: dbBooking.offer?.board || '' },
                    beginDate: dbBooking.offer?.checkIn,
                    endDate: dbBooking.offer?.checkOut,
                    night: dbBooking.offer?.nights || 0,
                    location: dbBooking.hotel.location || {}
                }] : []),
                // Flight services (type 2)
                ...(dbBooking.flights || []).map((f: any) => ({
                    type: 2,
                    flightNo: f.flightNumber || '',
                    airline: { name: resolveAirlineName(f.carrier, '', f.flightNumber) },
                    departure: { 
                        date: f.departureDate, 
                        airport: { code: f.departureAirport } 
                    },
                    arrival: { 
                        date: f.arrivalDate, 
                        airport: { code: f.arrivalAirport } 
                    },
                    direction: f.direction
                }))
            ],
            // Extra fields from DB booking for display
            _dbBooking: dbBooking
        };
    }
    
    return null;
};

export const cancelReservation = async (reservationNumber: string, reason: string, logApiCall: (log: ApiLogEntry) => void): Promise<any> => {
    const response = await fetch(`/api/booking-flow/${reservationNumber}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason })
    });
    const data = await response.json();

    if (logApiCall) {
        logApiCall({
            timestamp: new Date().toISOString(),
            endpoint: `/api/booking-flow/${reservationNumber}/cancel`,
            request: { reason },
            response: JSON.stringify(data),
            status: data.success ? 'success' : 'error'
        });
    }

    return data;
};

export const getReservationDocuments = async (reservationNumber: string, logApiCall: (log: ApiLogEntry) => void): Promise<any[]> => {
    const response = await fetch(`/api/booking-flow/${reservationNumber}/documents`);
    const data = await response.json();
    return data.documents || [];
};

export const getReservationDocument = async (reservationNumber: string, type: string, logApiCall: (log: ApiLogEntry) => void): Promise<any> => {
    const response = await fetch(`/api/booking-flow/${reservationNumber}/document/${type}`);
    const data = await response.json();
    return data.document;
};
